function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute("form", `${element.form.getAttribute("id")}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open"));

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute("aria-expanded", !(event.currentTarget.closest("details") || event.currentTarget.closest("custom-details")).hasAttribute("open"));
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (elementToFocus.tagName === "INPUT" && ["search", "text", "email", "url"].includes(elementToFocus.type) && elementToFocus.value) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ["ARROWUP", "ARROWDOWN", "ARROWLEFT", "ARROWRIGHT", "TAB", "ENTER", "SPACE", "ESCAPE", "HOME", "END", "PAGEUP", "PAGEDOWN"];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + "pauseVideo" + '","args":""}', "*");
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) => button.addEventListener("click", this.onButtonClick.bind(this)));
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === "minus") {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle("disabled", parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
window.theme = window.theme || {};
window.theme.Currency = (function () {
  var moneyFormat = "{{amount}}DKK";
  var superScript = theme && theme.settings && theme.settings.superScriptPrice;
  function formatMoney(cents, format) {
    if (!format) {
      format = theme.settings.moneyFormat;
    }
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || moneyFormat;
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = theme.utils.defaultTo(precision, 2);
      thousands = theme.utils.defaultTo(thousands, ",");
      decimal = theme.utils.defaultTo(decimal, ".");
      if (isNaN(number) || number == null) {
        return 0;
      }
      number = (number / 100.0).toFixed(precision);
      var parts = number.split(".");
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    }
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        if (superScript && value && value.includes(".")) {
          value = value.replace(".", "<sup>") + "</sup>";
        }
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        if (superScript && value && value.includes(",")) {
          value = value.replace(",", "<sup>") + "</sup>";
        }
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
    }
    return formatString.replace(placeholderRegex, value);
  }

  function getBaseUnit(variant) {
    if (!variant) {
      return;
    }
    if (!variant.unit_price_measurement || !variant.unit_price_measurement.reference_value) {
      return;
    }
    return variant.unit_price_measurement.reference_value === 1
      ? variant.unit_price_measurement.reference_unit
      : variant.unit_price_measurement.reference_value + variant.unit_price_measurement.reference_unit;
  }

  return {
    formatMoney: formatMoney,
    getBaseUnit: getBaseUnit,
  };
})();
theme.utils = {
  getQueryVariable: function (variable) {
    var query = window.location.search.substring(1);
    var vars = query.split("&");
    for (var i = 0; i < vars.length; i++) {
      var pair = vars[i].split("=");
      if (pair[0] == variable) {
        return pair[1];
      }
    }
    return false;
  },
  isMobile: function () {
    return navigator.userAgent.match(/(iPhone|iPod|Android|ios|SymbianOS|BlackBerry|webOS)/i);
  },
  defaultTo: function (value, defaultValue) {
    return value == null || value !== value ? defaultValue : value;
  },
};

if (typeof window.Shopify == "undefined") {
  window.Shopify = {
    productNav: {
      isOpen: false,
    },
    isMobile: function () {
      // 原有的用户代理检查
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      if (/android|iPad|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
        return true;
      }

      // 检查屏幕宽度
      if (window.innerWidth <= 768) {
        return true;
      }

      // 检查触摸支持
      if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
        return true;
      }

      // 使用媒体查询
      if (window.matchMedia("(max-width: 768px)").matches) {
        return true;
      }

      return false;
    },
  };
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options["hideElement"] || province_domid);

  Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) => summary.addEventListener("click", this.onSummaryClick.bind(this)));
    this.querySelectorAll("button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)").forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary")) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
      summaryElement.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener("transitionend", addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
      submenu.classList.remove("submenu-open");
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(detailsElement.closest("details[open]"), detailsElement.querySelector("summary"));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset = this.borderOffset || this.closest(".header-wrapper").classList.contains("header-wrapper--border-bottom") ? 1 : 0;
    document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header && document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener("click", this.hide.bind(this, false));
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (event.pointerType === "mouse" && !event.target.closest("deferred-media, product-model")) this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace("shopify-section-", "");
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, "text/html");
            const sourceQty = html.querySelector(".quick-order-list-container").parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`));
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(true));

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(content.querySelector("video, model-viewer, iframe"));
      console.log(deferredElement);
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == "VIDEO" && deferredElement.getAttribute("autoplay")) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.direction = this.getAttribute("data-direction") || "horizontal";

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    if (this.direction == "horizontal") {
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    } else {
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetTop - this.sliderItemsToShow[0].offsetTop;
      this.style.height = `${document.querySelector(".gallery-slider").clientHeight}px`;
    }
    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;
    if (this.direction == "horizontal") {
      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
        this.prevButton.setAttribute("disabled", "disabled");
      } else {
        this.prevButton.removeAttribute("disabled");
      }
    } else {
      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollTop === 0) {
        this.prevButton.setAttribute("disabled", "disabled");
      } else {
        this.prevButton.removeAttribute("disabled");
      }
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    if (this.direction == "horizontal") {
      const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
      return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
    } else {
      const lastVisibleSlide = this.slider.clientHeight + this.slider.scrollTop - offset;
      return element.offsetTop + element.clientHeight <= lastVisibleSlide && element.offsetTop >= this.slider.scrollTop;
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    if (this.direction == "horizontal") {
      this.slideScrollPosition = event.currentTarget.name === "next" ? this.slider.scrollLeft + step * this.sliderItemOffset : this.slider.scrollLeft - step * this.sliderItemOffset;
    } else {
      this.slideScrollPosition = event.currentTarget.name === "next" ? this.slider.scrollTop + step * this.sliderItemOffset : this.slider.scrollTop - step * this.sliderItemOffset;
    }
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    if (this.direction == "horizontal") {
      this.slider.scrollTo({
        left: position,
      });
    } else {
      this.slider.scrollTo({
        top: position,
      });
    }
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener("click", this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add("slider-counter__link--active");
    this.sliderControlButtons[this.currentPage - 1].setAttribute("aria-current", true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === "next" && !isLastSlide) || (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });

    // this.variant = JSON.parse(this.querySelector("[data-selected-variant]")?.innerHTML || null);
    // if(this.variant.inventory_quantity)
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target.closest(".product-form__input").querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", swatchValue);
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", "unset");
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty("--swatch-focal-point", target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset");
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener("storefront:signincompleted", this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace("[min]", event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace("[max]", event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace("[step]", event.target.step));
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

// ============dev by tobey=============

// 给所有popup-content属性不为空的元素添加监听鼠标移入事件，鼠标悬浮时以气泡的形式显示popup-content属性值
document.querySelectorAll("[popup-content]").forEach((element) => {
  // 判断是否是popup-content属性不为空的元素
  if (!element.getAttribute("popup-content")) return;
  element.addEventListener("mouseenter", () => {
    const popupContent = element.getAttribute("popup-content");
    const popup = document.createElement("div");
    popup.classList.add("popup");
    popup.innerHTML = popupContent;
    element.appendChild(popup);
  });
  element.addEventListener("mouseleave", () => {
    element.querySelector(".popup").remove();
  });
});

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.WSTORAGEKEY = "product:wishlist";
    this.CSTORAGEKEY = "product:compare";
    this.pid = this.dataset.productId;
    this.initWishlistButtonEvent();
    this.initCompareButtonEvent();
  }

  initWishlistButtonEvent() {
    const wishlistButtons = this.querySelector("[data-wishlist-add]");
    if (wishlistButtons) {
      let config = {
        button: wishlistButtons,
        type: "wishlist",
        key: this.WSTORAGEKEY,
      };
      this.initButtonState(config);
      wishlistButtons.addEventListener("click", this.wishlistToggleHandler.bind(this, config));
    }
  }

  initCompareButtonEvent() {
    const compareButtons = this.querySelector("[data-compare-add]");
    if (compareButtons) {
      let config = {
        button: compareButtons,
        type: "compare",
        key: this.CSTORAGEKEY,
      };
      this.initButtonState(config);
      compareButtons.addEventListener("click", this.compareToggleHandler.bind(this, config));
    }
  }

  initButtonState({ button, type, key }) {
    const list = JSON.parse(localStorage.getItem(key) || "[]");
    const inList = list.includes(this.pid);
    this.setStatus(button, type, inList);
  }

  wishlistToggleHandler({ button, type }) {
    let list = JSON.parse(localStorage.getItem(this.WSTORAGEKEY) || "[]");
    const index = list.indexOf(this.pid);

    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.push(this.pid);
    }

    localStorage.setItem(this.WSTORAGEKEY, JSON.stringify(list));
    this.setStatus(button, type, index < 0);

    document.dispatchEvent(
      new CustomEvent(`wishlist-changed`, {
        bubbles: true,
        detail: { count: list.length },
      })
    );
  }

  compareToggleHandler({ button, type }) {
    let list = JSON.parse(localStorage.getItem(this.CSTORAGEKEY) || "[]");
    const index = list.indexOf(this.pid);

    if (index > -1) {
      document.dispatchEvent(new CustomEvent("show:compare:popup"));
    } else {
      list.push(this.pid);
      localStorage.setItem(this.CSTORAGEKEY, JSON.stringify(list));
      this.setStatus(button, type, true);
      document.dispatchEvent(new CustomEvent("update:compare:popup"));
    }
  }

  setStatus(button, type, active) {
    if (type == "compare") {
      if (active) {
        document.addEventListener(`compare:remove:${button.getAttribute("data-product-id")}`, (event) => {
          console.log("收到比较产品移除事件");
          this.setStatus(button, "compare", false);
        });
      } else {
        document.removeEventListener(`compare:remove:${button.getAttribute("data-product-id")}`, (event) => {
          this.setStatus(button, "compare", false);
        });
      }
    }
    button.classList.toggle(`in-${type}`, active);
    button.setAttribute("aria-label", `${active ? "Fjern fra" : "Tilføj til"} ${type == "wishlist" ? "ønskeliste" : "sammenligne"}`);
    button.setAttribute(
      "popup-content",
      `${active ? (type == "compare" ? "Gå til sammenligning" : "Fjern formularens ønskeliste") : "Tilføj til " + (type == "wishlist" ? "ønskeliste" : "sammenligne")}`
    );
  }
}

customElements.define("product-card", ProductCard);

class WishlistCount extends HTMLElement {
  constructor() {
    super();
    this.count = this.getCount();
    this.setCount(this.count);

    // 定义wishlist-changed自定义事件
    document.addEventListener("wishlist-changed", (event) => {
      this.setCount(event.detail.count);
    });
  }

  getCount() {
    const count = JSON.parse(localStorage.getItem("product:wishlist"))?.length || 0;
    return count;
  }

  setCount(count) {
    if (!this.countElement) {
      this.countElement = this.querySelector("[data-wishlist-count]");
      this.querySelector("[data-wishlist-circle]").removeAttribute("style");
      this.countElement.removeAttribute("style");
    }
    this.count = count;
    this.countElement.textContent = count;
  }

  // 销毁组件时移除事件监听器
  disconnectedCallback() {
    document.removeEventListener("wishlist-changed");
  }
}

customElements.define("wishlist-count", WishlistCount);

var __defProp = Object.defineProperty,
  __defNormalProp = (obj, key, value) =>
    key in obj
      ? __defProp(obj, key, {
          enumerable: !0,
          configurable: !0,
          writable: !0,
          value,
        })
      : (obj[key] = value),
  __publicField = (obj, key, value) => (__defNormalProp(obj, typeof key != "symbol" ? key + "" : key, value), value),
  __accessCheck = (obj, member, msg) => {
    if (!member.has(obj)) throw TypeError("Cannot " + msg);
  },
  __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj)),
  __privateAdd = (obj, member, value) => {
    if (member.has(obj)) throw TypeError("Cannot add the same private member more than once");
    member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
  },
  __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value),
  __privateWrapper = (obj, member, setter, getter) => ({
    set _(value) {
      __privateSet(obj, member, value, setter);
    },
    get _() {
      return __privateGet(obj, member, getter);
    },
  }),
  __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);

var _requireSplit,
  _lastScreenWidth,
  _split,
  split_fn,
  _onWindowResized,
  onWindowResized_fn,
  SplitLines = class extends HTMLElement {
    constructor() {
      super(),
        __privateAdd(this, _split),
        __privateAdd(this, _onWindowResized),
        __privateAdd(this, _requireSplit, !0),
        __privateAdd(this, _lastScreenWidth, window.innerWidth),
        this.attachShadow({
          mode: "open",
        }),
        this.shadowRoot.appendChild(document.createRange().createContextualFragment("<slot></slot>")),
        window.addEventListener("resize", throttle(__privateMethod(this, _onWindowResized, onWindowResized_fn).bind(this))),
        new MutationObserver(__privateMethod(this, _split, split_fn).bind(this, !0)).observe(this, {
          characterData: !0,
          attributes: !1,
          childList: !1,
          subtree: !0,
        });
    }
    connectedCallback() {
      __privateMethod(this, _split, split_fn).call(this);
    }
    get lines() {
      return Array.from(this.shadowRoot.children);
    }
  };
(_requireSplit = new WeakMap()),
  (_lastScreenWidth = new WeakMap()),
  (_split = new WeakSet()),
  (split_fn = function (force = !1) {
    if (!__privateGet(this, _requireSplit) && !force) return;
    this.shadowRoot.innerHTML = this.textContent.replace(/./g, "<span>$&</span>").replace(/\s/g, " ");
    const bounds = new Map();
    Array.from(this.shadowRoot.children).forEach((letter) => {
      const key = Math.round(letter.getBoundingClientRect().top);
      bounds.set(key, (bounds.get(key) || "").concat(letter.textContent));
    }),
      this.shadowRoot.replaceChildren(
        ...Array.from(bounds.values(), (line) =>
          document.createRange().createContextualFragment(
            `
    <span style="display: inline-block;">
      <span style="display: block">${line}</span>
    </span>
  `
          )
        )
      ),
      __privateSet(this, _requireSplit, !1);
  }),
  (_onWindowResized = new WeakSet()),
  (onWindowResized_fn = function () {
    __privateGet(this, _lastScreenWidth) !== window.innerWidth && (__privateMethod(this, _split, split_fn).call(this, !0), __privateSet(this, _lastScreenWidth, window.innerWidth));
  }),
  window.customElements.get("split-lines") || window.customElements.define("split-lines", SplitLines);

window.themeVariables = {
  settings: {
    headingApparition: "split_clip",
  },
};
function getHeadingKeyframe(element, options = {}) {
  if (!element) return [];
  const splitLines = element.querySelector("split-lines")?.lines;
  if (window.themeVariables.settings.headingApparition === "fade" || !splitLines)
    return [
      element,
      {
        opacity: [0, 1],
      },
      {
        duration: 0.2,
        ...options,
      },
    ];
  switch (((element.style.opacity = "1"), window.themeVariables.settings.headingApparition)) {
    case "split_fade":
      return [
        splitLines,
        {
          transform: ["translateY(0.5em)", "translateY(0)"],
          opacity: [0, 1],
        },
        {
          duration: 0.3,
          delay: stagger2(0.1),
          ...options,
        },
      ];
    case "split_clip":
      return [
        splitLines,
        {
          clipPath: ["inset(0 0 100% 0)", "inset(0 0 -0.3em 0)"],
          transform: ["translateY(100%)", "translateY(0)"],
          opacity: [0, 1],
        },
        {
          duration: 0.7,
          delay: stagger2(0.15),
          easing: [0.22, 1, 0.36, 1],
          ...options,
        },
      ];
    case "split_rotation":
      const rotatedSpans = splitLines.map((line) => line.querySelector("span"));
      return (
        rotatedSpans.forEach((span) => (span.style.transformOrigin = "top left")),
        splitLines.forEach((line) => (line.style.clipPath = "inset(0 0 -0.3em 0)")),
        [
          rotatedSpans,
          {
            transform: ["translateY(0.5em) rotateZ(5deg)", "translateY(0) rotateZ(0)"],
            opacity: [0, 1],
          },
          {
            duration: 0.4,
            delay: stagger2(0.1),
            ...options,
          },
        ]
      );
  }
}

function throttle2(callback) {
  let requestId = null,
    lastArgs;
  const later = (context) => () => {
      (requestId = null), callback.apply(context, lastArgs);
    },
    throttled = (...args) => {
      (lastArgs = args), requestId === null && (requestId = requestAnimationFrame(later(this)));
    };
  return (
    (throttled.cancel = () => {
      cancelAnimationFrame(requestId), (requestId = null);
    }),
    throttled
  );
}

/**
 * 检测元素是否进入视图,并在元素可见时触发回调函数
 * @param {HTMLElement} element - 要观察的元素
 * @param {Function} callback - 元素进入视图时要调用的回调函数
 * @param {Object} [options] - IntersectionObserver的配置选项
 */
function inView3(element, callback, options = {}) {
  // 默认选项
  const defaultOptions = {
    root: null, // 使用视口作为根
    rootMargin: "0px", // 根元素的边距
    threshold: 0, // 目标元素有任何部分可见就触发
  };

  // 合并默认选项和用户提供的选项
  const observerOptions = { ...defaultOptions, ...options };

  // 创建一个 IntersectionObserver 实例
  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // 元素进入视图
        callback(entry.target);

        // 如果只需要触发一次,可以在这里取消观察
        // observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  // 开始观察目标元素
  observer.observe(element);

  // 返回 observer 实例,以便可以在外部控制观察的结束
  return observer;
}

// import {
//   animate as animate3,
//   timeline as timeline5,
//   inView as inView3
// } from "//hifi-freaks.dk/cdn/shop/t/6/assets/vendor.min.js";

var ImagesWithTextScrolling = class extends HTMLElement {
  connectedCallback() {
    inView3(this, this._reveal.bind(this)),
      this.hasAttribute("scrolling-experience") &&
        ((this._imageToTransitionItems = Array.from(this.querySelectorAll(".images-scrolling-desktop__media-wrapper > :not(:first-child)"))),
        (this._mainImage = this.querySelector(".images-scrolling-desktop__media-wrapper > :first-child")),
        (this._contentItems = Array.from(this.querySelectorAll(".images-scrolling__content"))),
        this._imageToTransitionItems.length > 0 && window.addEventListener("scroll", throttle2(this._onScroll.bind(this))));
  }
  _reveal() {
    Array.from(this.querySelectorAll('[reveal-on-scroll="true"]')).forEach((heading) => {
      animate3(...getHeadingKeyframe(heading));
    });
  }
  _onScroll() {
    const imageRect = this._mainImage.getBoundingClientRect(),
      imageBottom = imageRect.bottom,
      imageEffect = this.getAttribute("scrolling-experience");
    for (const [index, contentItem] of this._contentItems.entries()) {
      const contentItemRect = contentItem.getBoundingClientRect(),
        image = this._imageToTransitionItems[index - 1],
        content = this._contentItems[index];
      if (contentItemRect.top < imageBottom - imageRect.height * 0.15 && contentItemRect.bottom > imageBottom) {
        image &&
          !image.classList.contains("is-visible") &&
          (image.classList.add("is-visible"),
          imageEffect === "fade"
            ? timeline5([
                [
                  image,
                  {
                    opacity: [null, 1],
                  },
                  {
                    duration: 0.2,
                  },
                ],
                [
                  content,
                  {
                    opacity: [null, 1],
                  },
                  {
                    duration: 0.45,
                    at: "<",
                  },
                ],
              ])
            : timeline5([
                [
                  image,
                  {
                    opacity: [null, 1],
                    clipPath: ["inset(100% 0 0 0)", "inset(0 0 0 0)"],
                  },
                  {
                    duration: 0.35,
                    easing: [0.99, 0.01, 0.5, 0.94],
                    opacity: {
                      duration: 0,
                    },
                  },
                ],
                [
                  content,
                  {
                    opacity: [null, 1],
                  },
                  {
                    duration: 0.45,
                    at: "<",
                  },
                ],
              ]));
        break;
      }
      if (contentItemRect.top > imageBottom - imageRect.height * 0.15) {
        image &&
          image.classList.contains("is-visible") &&
          (image.classList.remove("is-visible"),
          imageEffect === "fade"
            ? timeline5([
                [image, { opacity: [null, 0] }, { duration: 0.2 }],
                [content, { opacity: [null, 0] }, { duration: 0.2, at: "<" }],
              ])
            : timeline5([
                [
                  image,
                  {
                    opacity: [null, 1],
                    clipPath: ["inset(0 0 0 0)", "inset(100% 0 0 0)"],
                  },
                  {
                    duration: 0.35,
                    easing: [0.99, 0.01, 0.5, 0.94],
                    opacity: { duration: 0 },
                  },
                ],
                [content, { opacity: [null, 0] }, { duration: 0.2, at: "<" }],
              ]));
        break;
      }
    }
  }
};
window.customElements.get("images-with-text-scrolling") || window.customElements.define("images-with-text-scrolling", ImagesWithTextScrolling);

customElements.get("collection-products") ||
  customElements.define(
    "collection-products",
    class extends HTMLElement {
      constructor() {
        super();
        this.rows = {
          2: 16,
          3: 15,
          4: 16,
          5: 20,
          6: 24,
        };
        this.STORAGEKEY = "collection:columns";
        this.url = this.getAttribute("data-url");
      }

      connectedCallback() {
        this.list = this.querySelector("#product-grid");
        // 订阅collection-column-change事件
        document.addEventListener("collection-column-change", (event) => {
          this.handleColumnChange(event.detail.columns);
        });
      }

      handleColumnChange(columns) {
        columns = columns || localStorage.getItem(this.STORAGEKEY) || 4;
        // 给this.list移除*-col-desktop类，根据columns添加*-col-desktop类
        this.list.classList.remove("grid--2-col-desktop", "grid--3-col-desktop", "grid--4-col-desktop", "grid--5-col-desktop", "grid--6-col-desktop");
        this.list.classList.add(`grid--${columns}-col-desktop`);

        // this.fetchCollectionProducts(theme.utils.getQueryVariable("page") || 1, this.rows[columns]);
      }

      // 修改当前浏览器URL
      updateCurrentURL(pageIndex, pageSize) {
        const url = new URL(window.location.href);
        if (pageIndex > 1) {
          url.searchParams.set("page", pageIndex);
        }
        url.searchParams.set("page-size", pageSize);
        history.pushState({}, "", url.toString());
      }

      fetchCollectionProducts(pageIndex, pageSize) {
        this.updateCurrentURL(pageIndex, pageSize);
        fetch(`${this.url}?section_id=main-collection-product-grid&page=${pageIndex}&page-size=${pageSize}&t=${Date.now()}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, "text/html");
            console.log("产品数量：", html.querySelectorAll("#product-grid > .grid__item")?.length);
            this.list.innerHTML = html.querySelector("#product-grid").innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }

      disconnectedCallback() {
        // 取消订阅column-change事件
        document.removeEventListener("collection-column-change", this.handleColumnChange);
      }
    }
  );

customElements.get("column-switcher") ||
  customElements.define(
    "column-switcher",
    class extends HTMLElement {
      constructor() {
        super();
        this.STORAGEKEY = "collection:columns";
      }
      connectedCallback() {
        this.tabButtons = this.querySelectorAll("[data-columns]");
        this.setColumns();
        this.initButtonEvent();
      }

      initButtonEvent() {
        this.tabButtons.forEach((button) => {
          button.addEventListener("click", () => {
            this.setColumns(button.dataset.columns);
          });
        });
      }

      setColumns(columns) {
        // 判断columns是否存在，如果不存在则获取localStorage里的columns值，否则设置为4
        columns = columns || localStorage.getItem(this.STORAGEKEY) || 4;

        // 设置localStorage里的columns值
        localStorage.setItem(this.STORAGEKEY, columns);
        this.tabButtons.forEach((button) => {
          button.classList.toggle("active", button.getAttribute("data-columns") === String(columns));
        });

        document.dispatchEvent(
          new CustomEvent("collection-column-change", {
            detail: {
              columns,
            },
          })
        );
      }
    }
  );

class FestSelect extends HTMLElement {
  constructor() {
    super();
    this.openStatus = false;
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.render();
    this.addEventListeners();
  }

  render() {
    this.select = this.querySelector("select");
    if (!this.select) return;

    const options = Array.from(this.select.options);
    const selectedOption = options.find((option) => option.selected) || options[0];
    this.value = selectedOption.value;
    this.shadowRoot.innerHTML = `
      <style>
        .fest-select {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0;
          border-radius: 4px;
          cursor: pointer;
          line-height: 1;
        }
        .selected-option {
          flex-grow: 1;
        }
        .arrow {
          display: flex;
          font-size: 16px;
          transition: all .3s;

          transform: translateY(1px);
        }
        [open] .arrow{

          transform: rotate(180deg) translateY(1px);
        }
      </style>
      <div class="fest-select">
        <div class="selected-option">${selectedOption.textContent}</div>
        <div class="arrow">
          <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
        <slot name="save"></slot>
      </div>
    `;
  }

  open() {
    if (!this.dropdownDom) {
      this.dropdownDom = this.createDropdownDom();
      document.body.appendChild(this.dropdownDom);
    }
    this.festSelect.setAttribute("open", "");
    this.positionDropdown();
    this.openStatus = true;
    this.dropdownDom.style.display = "block";
    this.windowResizeLinstener();
  }

  close() {
    if (!this.dropdownDom) return;
    this.festSelect.removeAttribute("open");
    this.openStatus = false;
    this.dropdownDom.style.display = "none";
    this.removeWindowResizeListener();
  }

  addEventListeners() {
    this.festSelect = this.shadowRoot.querySelector(".fest-select");
    this.festSelect.addEventListener("click", () => this.toggleDropdown());
  }

  toggleDropdown() {
    if (this.openStatus) {
      this.close();
    } else {
      this.open();
      // 点击外部关闭下拉列表
      document.addEventListener("click", this.closeDropdown);
    }
  }

  createDropdownDom() {
    const dropdown = document.createElement("div");
    dropdown.className = "fest-select-dropdown";
    const dropdown_list = document.createElement("ul");
    dropdown_list.className = "fest-select-dropdown__list";

    Array.from(this.select.options).forEach((option) => {
      const item = document.createElement("div");
      item.className = "fest-select-dropdown__item";
      if (option.selected) item.classList.add("active");
      item.setAttribute("value", option.value);
      item.textContent = option.textContent;
      item.addEventListener("click", () => this.selectOption(option));
      dropdown_list.appendChild(item);
    });
    dropdown.appendChild(dropdown_list);

    return dropdown;
  }

  positionDropdown() {
    const rect = this.getBoundingClientRect();
    this.dropdownDom.style.position = "fixed";
    this.dropdownDom.style.top = `${rect.top + rect.height + 16}px`;
    this.dropdownDom.style.left = `${rect.left}px`;
    // this.dropdownDom.style.width = `${rect.width}px`;
  }

  selectOption(option) {
    this.select.value = option.value;
    this.select.dispatchEvent(new Event("input", { bubbles: true, cancelable: true }));
    this.shadowRoot.querySelector(".selected-option").textContent = option.textContent;
    this.closeDropdown();
  }

  closeDropdown = (event) => {
    if (!event || !this.contains(event.target)) {
      if (this.openStatus) {
        this.close();
        document.removeEventListener("click", this.closeDropdown);
      }
    }
  };

  // 监听窗口变化
  windowResizeLinstener() {
    window.addEventListener("resize", this.positionDropdown.bind(this));
    // 监听body滚动
    document.addEventListener("scroll", this.positionDropdown.bind(this));
  }

  // 移除窗口变化监听
  removeWindowResizeListener() {
    window.removeEventListener("resize", this.positionDropdown.call(this));
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.closeDropdown);
    this.removeWindowResizeListener();
  }
}

customElements.define("fest-select", FestSelect);

class ComparePopup extends HTMLElement {
  constructor() {
    super();
    this.STORAGEKEY = "product:compare";
  }
  connectedCallback() {
    // 移除产品事件
    this.querySelectorAll("[data-remove-compare]").forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        this.removeCompareItemByID(el.dataset.id);
        e.target.closest(".compare-item").remove();

        document.dispatchEvent(new CustomEvent(`compare:remove:${el.dataset.id}`));
      });
    });
    // 清空所有产品事件
    this.querySelector("[data-clear-compare]").addEventListener("click", (e) => {
      e.preventDefault();
      (JSON.parse(localStorage.getItem(this.STORAGEKEY)) || []).forEach((id) => {
        document.dispatchEvent(new CustomEvent(`compare:remove:${id}`));
      });

      localStorage.removeItem(this.STORAGEKEY);
      this.close();
      this.querySelectorAll(".compare-item").forEach((el) => el.remove());
    });
    // 关闭弹窗事件
    this.querySelector("[data-close-compare]").addEventListener("click", (e) => {
      e.preventDefault();
      this.close();
    });
  }
  removeCompareItemByID(id) {
    const compareList = JSON.parse(localStorage.getItem(this.STORAGEKEY)) || [];
    localStorage.setItem(this.STORAGEKEY, JSON.stringify(compareList.filter((e) => e != id)));
  }
  close() {
    this.removeAttribute("open");
  }
  open() {
    this.setAttribute("open", "");
  }
}

customElements.define("compare-popup", ComparePopup);

class CompareWidget extends HTMLElement {
  constructor() {
    super();
    this.STORAGEKEY = "product:compare";
  }

  connectedCallback() {
    document.addEventListener("show:compare:popup", () => {
      this.showPopup();
    });
    document.addEventListener("update:compare:popup", () => {
      this.fetchComparePopup();
    });
  }

  showPopup() {
    if (this.comparePopup) {
      this.open();
    } else {
      this.fetchComparePopup();
    }
  }

  fetchComparePopup() {
    let compare_ids = JSON.parse(localStorage.getItem(this.STORAGEKEY)) || [];
    if (!compare_ids?.length) alert("Vælg produktet først");
    let fetchUrl = `/search/?section_id=widget-compare-popup&type=product&options[unavailable_products]=last&q=${compare_ids.map((id) => `id:${id}`).join("%20OR%20")}`;
    fetch(fetchUrl)
      .then((response) => response.text())
      .then((html) => {
        // 将html转成DOM对象并取出compare-popup-container
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const comparePopupContainer = doc.querySelector("compare-popup");
        if (comparePopupContainer) {
          // 将comparePopupContainer插入到compare-widget中
          this.innerHTML = comparePopupContainer.outerHTML;
          this.comparePopup = this.querySelector("compare-popup");
          this.open();
        }
      });
  }

  open() {
    this.comparePopup.setAttribute("open", "");
  }

  close() {
    this.comparePopup.removeAttribute("open");
  }
}

customElements.define("compare-widget", CompareWidget);

class BackToTop extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    // this.currentScrollTop = 0;
    // this.preventReveal = false;
    this.onScrollHandler();
    window.addEventListener("scroll", this.onScrollHandler.bind(this), false);
  }

  onScrollHandler() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop < window.innerHeight) {
      this.hide();
    } else {
      this.reveal();
    }
    // if (scrollTop > this.currentScrollTop) {
    //   requestAnimationFrame(this.hide.bind(this));
    // } else {
    //   if (!this.preventReveal) {
    //     requestAnimationFrame(this.reveal.bind(this));
    //   } else {
    //     window.clearTimeout(this.isScrolling);
    //     this.isScrolling = setTimeout(() => {
    //       this.preventReveal = false;
    //     }, 66);
    //     requestAnimationFrame(this.hide.bind(this));
    //   }
    // }
    // this.currentScrollTop = scrollTop;
  }

  hide() {
    this.classList.remove("back-to-top--visible");
  }

  reveal() {
    this.classList.add("back-to-top--visible");
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScrollHandler, false);
  }
}

customElements.define("back-to-top", BackToTop);

class GSwiper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.swiperEl = this.querySelector(".swiper");
    this.config = {
      autoplay: Boolean(this.getAttribute("autoplay")),
      loop: Boolean(this.getAttribute("loop")) || false,
      speed: Number(this.getAttribute("speed")) || 300,
      spaceBetween: 10,
      slidesPerView: Number(this.getAttribute("m-slidesperview")) || 1.2,
      breakpoints: {
        640: {
          slidesPerView: 2.2,
          spaceBetween: Number(this.getAttribute("spaceBetween")) || 20,
        },
        1280: {
          slidesPerView: 3.2,
        },
        1280: {
          slidesPerView: Number(this.getAttribute("slidesPerView")) || 4.2,
        },
      },
      on: {
        init: () => {
          this.querySelector(".swiper-pagination").style.setProperty("--slide", this.querySelectorAll(".swiper-pagination-bullet").length);
        },
        slideChange: () => {
          this.querySelector(".swiper-pagination").style.setProperty("--slide-index", this.getActiveBulletIndex());
        },
      },
    };
    if (Boolean(this.getAttribute("navigation"))) {
      this.config.navigation = {
        nextEl: this.querySelector(".swiper-button-next"),
        prevEl: this.querySelector(".swiper-button-prev"),
      };
    }
    if (Boolean(this.getAttribute("pagination"))) {
      this.config.pagination = {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
      };
    }
    this.initSwiper();
  }

  initSwiper() {
    this.swiper = new Swiper(this.swiperEl, this.config);
  }

  getActiveBulletIndex() {
    // 获取所有的 pagination bullet 元素
    if (!this.bullets) this.bullets = this.querySelectorAll(".swiper-pagination-bullet");

    // 转换为数组并查找 active bullet 的索引
    const index = Array.from(this.bullets).findIndex((bullet) => bullet.classList.contains("swiper-pagination-bullet-active"));

    // 返回索引（如果没找到则返回 -1）
    return index;
  }

  disconnectedCallback() {
    this.swiper.destroy();
  }
}

customElements.define("g-swiper", GSwiper);

class Trigger extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.render();

    this.initEvents();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        @keyframes rotate-loading {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        [loading="true"] .default,
        [loading="false"] .loading{
          display: none !important;
        }
        [loading="true"] .loading,
        [loading="false"] .default{
          display: block !important;
        }
        .default{
          cursor: pointer;
        }
        .loading{
          cursor: wait;
          animation: rotate-loading 1s linear infinite;
        }
        .loading, .default{ font-size: 0px; }
        
      </style>
      <div class="wrapper" loading="false">
        <div class="default">
          <slot name="default"></slot>
        </div>
        <div class="loading">
          <slot name="loading"></slot>
        </div>
      </div>
    `;
    this.querySelector(".hidden").classList.remove("hidden");
  }

  initEvents() {
    this.wrapper = this.shadowRoot.querySelector(".wrapper");

    this.shadowRoot.querySelector(".default").addEventListener("click", () => {
      this.loading();
      this.onLoading && this.onLoading();
    });
  }

  loading() {
    this.wrapper.setAttribute("loading", "true");
  }

  stop() {
    this.wrapper.setAttribute("loading", "false");
  }
}

customElements.define(
  "demo-trigger",
  class extends Trigger {
    constructor() {
      super();
      this.loaded = false;
      this.handle = this.getAttribute("data-handle");
    }

    async onLoading() {
      try {
        if (this.loaded) {
          this.show();
          return;
        }
        const section = await this.fetchDemoProduct();
        console.log(section);
        this.drawer = new Shopify.Drawer({
          el: section.querySelector(".demo-container"),
          styles: "width: 80rem;",
        });
        this.show();
        this.loaded = true;
      } catch (error) {
        console.log(error);
      }
    }

    show() {
      this.stop();
      this.drawer.open();
    }

    fetchDemoProduct() {
      if (!this.handle) return Promise.reject("product handle not found");

      const demoSectionUrl = `/products/${this.handle}?section_id=product-all-demo-preview`;

      return new Promise((resolve, reject) => {
        fetch(demoSectionUrl)
          .then((response) => response.text())
          .then((text) => {
            const sectionInnerHTML = new DOMParser().parseFromString(text, "text/html").querySelector(".shopify-section");
            resolve(sectionInnerHTML);
          })
          .catch((e) => {
            reject(e);
          });
      });
    }
  }
);

// document.querySelectorAll('custom-details[trigger="hover"]').forEach((details) => {
//   const saEvent = () => {
//     const status = details.hasAttribute('open');
//     details.querySelector("summary").setAttribute('aria-expanded', !status);
//     if(status){
//       details.removeAttribute('open');
//     }else{
//       details.setAttribute('open', '');
//     }
//   }

//   details.addEventListener("mouseenter", saEvent);
//   details.addEventListener("mouseleave", saEvent);
// });

class CustomDetails extends HTMLElement {
  constructor() {
    super();
    this.off = null;
  }
  connectedCallback() {
    this.trigger = this.getAttribute("trigger");
    this.summary = this.querySelector("summary");
    this.dialog = this.closest("sticky-header").querySelector(".header-dialog");

    this.initEvent(this.trigger);
  }

  initEvent(trigger) {
    // 添加事件
    const saEvent = (event) => {
      event.stopPropagation();
      this.hasAttribute("open") ? this.close() : this.show();
    };

    this.off && this.off();

    if (trigger == "hover") {
      this.addEventListener("mouseenter", saEvent);
      this.addEventListener("mouseleave", saEvent);

      this.off = () => {
        this.removeEventListener("mouseenter", saEvent);
        this.removeEventListener("mouseleave", saEvent);
      };
    } else {
      this.summary.addEventListener("click", saEvent);
      this.off = () => {
        this.summary.removeEventListener("click", saEvent);
      };
    }
  }

  show() {
    this.summary.setAttribute("aria-expanded", true);
    this.setAttribute("open", "");
    this.dispatchEvent(new CustomEvent("toggle"));
    document.dispatchEvent(new CustomEvent("header:menu:toggle", { detail: "open" }));

    if (this.trigger == "click") {
      const all_open_details = document.querySelectorAll('[id^="Details-"][open]');
      // 遍历all_open_details除了自身以外的details全部关闭
      all_open_details.length &&
        all_open_details.forEach((details) => {
          if (details != this) {
            details.close(false);
          }
        });
    }
  }

  close(deep = true) {
    this.summary.setAttribute("aria-expanded", false);
    this.removeAttribute("open");
    this.dispatchEvent(new CustomEvent("toggle"));

    // deep: 深度关闭将彻底关闭dialog， 否则只关闭自身不关闭dialog
    deep && document.dispatchEvent(new CustomEvent("header:menu:toggle", { detail: "close" }));
  }

  static get observedAttributes() {
    return ["trigger"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name == "trigger") {
      this.off && this.initEvent(newValue);
    }
  }

  disconnectedCallback() {
    this.off && this.off();
  }
}

customElements.define("custom-details", CustomDetails);

class GDetails extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.render();
    this.details = this.shadowRoot.querySelector("details");
    this.summary = this.shadowRoot.querySelector("summary");
    this.content = this.shadowRoot.querySelector(".content");
    this.name = this.getAttribute("name");

    this.triggerMode = this.getAttribute("trigger") === "hover" ? "hover" : "click";
    this.addEventListeners();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
        }
        details {
          cursor: pointer;
        }
      </style>
      <details>
        <summary>
          <div>
            <slot name="summary"></slot>
            <slot name="icon"></slot>
          </div>
        </summary>
        <div class="content"><slot></slot></div>
      </details>
    `;
  }

  addEventListeners() {
    if (this.triggerMode === "click") {
      this.detailGroup = this.name ? document.querySelectorAll(`g-details[name=${this.name}]`) : [];
      this.summary.addEventListener("click", this.onClick.bind(this));
    } else {
      this.summary.addEventListener("mouseenter", this.onMouseEnter.bind(this));
      this.summary.addEventListener("mouseleave", this.onMouseLeave.bind(this));
    }
  }

  onClick(event) {
    event.preventDefault();
    this.toggleDetails();
  }

  onMouseEnter() {
    this.openDetails();
  }

  onMouseLeave() {
    this.closeDetails();
  }

  toggleDetails() {
    if (this.details.open) {
      this.closeDetails();
    } else {
      this.openDetails();
    }
  }

  openDetails() {
    if (this.triggerMode == "click" && this.detailGroup?.length) {
      this.detailGroup.forEach((el) => {
        if (el !== this && el.hasAttribute("open")) {
          el.removeAttribute("open");
        }
      });
    }
    this.details.open = true;
  }

  closeDetails() {
    this.details.open = false;
  }

  // 添加属性观察器，以便动态更改触发模式
  static get observedAttributes() {
    return ["trigger"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "trigger" && oldValue !== newValue) {
      this.triggerMode = newValue === "hover" ? "hover" : "click";
      this.removeEventListeners();
      this.addEventListeners();
    }
  }

  removeEventListeners() {
    this.summary.removeEventListener("click", this.onClick);
    this.summary.removeEventListener("mouseenter", this.onMouseEnter);
    this.summary.removeEventListener("mouseleave", this.onMouseLeave);
  }
}
/*====================================

<!-- 悬浮触发模式 -->
<g-details trigger="hover">
  <span slot="summary">悬浮展开</span>
  <p>这里是详细内容</p>
</g-details>

====================================*/

customElements.define("g-details", GDetails);

customElements.define(
  "count-down",
  class extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.time = this.getAttribute("data-time");
      if (!this.time) return;
      this.hours = this.querySelector("[hours]");
      this.minutes = this.querySelector("[minutes]");
      this.seconds = this.querySelector("[seconds]");

      this.targetDate = new Date(this.time).getTime();
      this.initCountdown();
    }

    initCountdown() {
      this.updateCountdown();
      this.interval = setInterval(() => this.updateCountdown(), 1000);
    }

    updateCountdown() {
      const now = new Date().getTime();
      const distance = this.targetDate - now;

      if (distance < 0) {
        clearInterval(this.interval);
        this.innerHTML = "倒计时结束";
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)) + days * 24;
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (this.hours) this.hours.textContent = this.padZero(hours);
      if (this.minutes) this.minutes.textContent = this.padZero(minutes);
      if (this.seconds) this.seconds.textContent = this.padZero(seconds);
    }

    padZero(num) {
      return num.toString().padStart(2, "0");
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }
  }
);
// 如果需要用到Shopify API需继承此类
class ClinetHTMLElement extends HTMLElement {
  constructor() {
    super();
  }

  async initClinet() {
    if (!Shopify.client) {
      await this.loadStorefrontAPI();
      Shopify.client = ShopifyStorefrontAPIClient.createStorefrontApiClient({
        storeDomain: Shopify.shop,
        apiVersion: "2025-01",
        publicAccessToken: "78dbef5026ec011a0c50d6db67199f50",
      });
      return Promise.resolve();
    }
    return Promise.resolve();
  }

  loadStorefrontAPI() {
    return new Promise((resolve, reject) => {
      if (window.ShopifyStorefrontAPIClient) {
        resolve();
      } else {
        const script = document.createElement("script");
        script.src = storefront_api_client_url;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      }
    });
  }
}
class ProductDemo extends ClinetHTMLElement {
  constructor() {
    super();
    this.demo_products = null;
    this.current_demo = null;
    this.attachShadow({ mode: "open" });
    this.render();
  }

  async connectedCallback() {
    this.variant_id = this.getAttribute("variant-id");

    const variants = this.getVariants();
    this.variantsSku = variants ? variants.map((e) => ({ id: e.id, sku: e.sku })) : [];
    console.log("this.variantsSku", this.variantsSku);

    await this.initClinet();
    this.fetchDemoProducts();
    // this.fetchProductsByVariantsIDs();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <slot></slot>
    `;
  }

  async fetchDemoProducts(sku) {
    const query = `
      query getProductsBySKU($query: String!) {
        products(first: 250, query: $query) {
          edges {
            node {
              id
              title
              handle
              description
              variants(first: 250) {
                edges {
                  node {
                    id
                    sku
                    title
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    image {
                      url
                      altText
                      width
                      height
                    }
                    quantityAvailable
                    storeAvailability(first: 20) {
                      edges {
                        node {
                          available
                          quantityAvailable
                          pickUpTime
                          location {
                            id
                            name
                          }
                        }
                      }
                    }
                  }
                }
              }
              images(first: 5) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      }
    `;

    const skus = sku ? sku : this.variantsSku.map((e) => `${e.sku}-demo`).join(" OR ");
    const variables = {
      // query: `variant:sku:WH-063693-00A`
      query: `variant:sku:${skus}`,
    };

    console.log("fetchDemoProducts:variables", variables);

    try {
      const response = await Shopify.client.request(query, { variables });
      console.log("fetchDemoProducts", response);
      if (response?.data?.products?.edges?.length) {
        this.demo_products = {};
        const products = response.data.products.edges.map((edge) => edge.node);
        products.forEach((item) => {
          let product = {
            ...item,
            variants: item.variants.edges.map((e) => ({
              ...e.node,
              price: {
                amount: parseFloat(e.node.price.amount).toFixed(2),
                currencyCode: e.node.price.currencyCode,
              },
            })),
          };
          console.log("sku", product.variants[0].sku);
          // 检查是否有库存
          if (product.variants[0].quantityAvailable) {
            const variant = this.variantsSku.find((e) => `${e.sku}-demo`.toLocaleLowerCase() == product.variants[0].sku.toLocaleLowerCase());
            if (variant) this.demo_products[variant.id] = product;
          }
        });
        console.log(this.demo_products);
        // this.updateDemoProduct();
        this.renderDemoProduct();
      } else {
        this.clear();
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }

  async fetchDemoVariants(sku) {
    const query = `
      query getProductsBySKU($query: String!) {
        products(first: 250, query: $query) {
          edges {
            node {
              id
              title
              handle
              description
              variants(first: 250) {
                edges {
                  node {
                    id
                    sku
                    title
                    availableForSale
                    currentlyNotInStock
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    image {
                      url
                      altText
                    }
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
              }
              images(first: 5) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      }
    `;

    // 构建精确匹配的 SKU 查询字符串
    const skuQueryString = sku ? `variant:sku:'${sku}'` : this.variantsSku.map((e) => `variant:sku:'${e.sku}'`).join(" OR ");

    const variables = {
      query: skuQueryString,
    };

    console.log("Query variables:", variables);

    try {
      const response = await Shopify.client.request(query, { variables });
      console.log("Demo products response:", response);
      if (response?.data?.products?.edges) {
        this.demo_products = response.data.products.edges.flatMap((productEdge) => {
          return productEdge.node.variants.edges
            .filter((variantEdge) => {
              return sku ? variantEdge.node.sku === sku : this.variantsSku.some((v) => v.sku === variantEdge.node.sku);
            })
            .map((variantEdge) => {
              const variant = variantEdge.node;
              return {
                ...variant,
                price: {
                  ...variant.price,
                  amount: parseFloat(variant.price.amount).toFixed(2),
                },
                compareAtPrice: variant.compareAtPrice
                  ? {
                      ...variant.compareAtPrice,
                      amount: parseFloat(variant.compareAtPrice.amount).toFixed(2),
                    }
                  : null,
                product: {
                  id: productEdge.node.id,
                  title: productEdge.node.title,
                  handle: productEdge.node.handle,
                  description: productEdge.node.description,
                  images: productEdge.node.images,
                },
              };
            });
        });

        console.log("Demo products:", this.demo_products);
        this.updateDemoProductsDisplay();
      } else {
        console.log("No products found for the given SKUs");
        this.demo_products = [];
        this.updateDemoProductsDisplay();
      }
    } catch (error) {
      console.error("Error fetching demo products:", error);
      if (error.graphQLErrors) {
        console.error("GraphQL Errors:", error.graphQLErrors);
      }
      if (error.networkError) {
        console.error("Network Error:", error.networkError);
      }
    }
  }

  async fetchProductByHandle(handle) {
    const query = `
      query getProductByHandle($handle: String!) {
        product(handle: $handle) {
          id
          title
          description
          variants(first: 5) {
            edges {
              cursor
              node {
                id
                title
                quantityAvailable
                price {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      handle,
    };

    console.log("Query variables:", variables);

    try {
      const response = await Shopify.client.request(query, { variables });
      console.log("Demo products response:", response);
    } catch (error) {
      console.error("Error fetching demo products:", error);
      if (error.graphQLErrors) {
        console.error("GraphQL Errors:", error.graphQLErrors);
      }
      if (error.networkError) {
        console.error("Network Error:", error.networkError);
      }
    }
  }

  updateDemoProduct() {
    console.log(this.variant_id);
    if (!this.demo_products[this.variant_id]) {
      this.clear();
      return;
    }
    this.current_demo = this.demo_products[this.variant_id];
    this.shadowRoot.innerHTML = `
      <style>
        .product-demo{
          padding: 10px 0;
        }
        .title{
          font-size: 18px;
          line-height: 24px;
        }
        .footer{
          display: flex;
          align-items: center;
          gap: 25px;
          margin-top: 10px;
          /* justify-content: space-between; */
        }
        .price{
          color: #DC2935;
          font-size: 20px;
          line-height: 24px;
        }
        .link{
          font-size: 15px;
          border-radius: 4px;
          padding: 0px 14px;
          line-height: 30px;
          text-decoration: none;
          font-weight: 500;
          background: #f9d300;
          color: #000;
        }
      </style>
      <div class="product-demo">
        <div class="title">${this.current_demo.title}</div>
        <div class="footer">
          <div class="price">${theme.Currency.formatMoney(this.current_demo.variants[0].price.amount, `{{amount}} ${this.current_demo.variants[0].price.currencyCode}`)}</div>
          <a class="link" href="/products/${this.current_demo.handle}">Go Now</a>
        </div>
      </div>
    `;
  }

  renderDemoProduct() {
    if (!this.demo_products) {
      this.clear();
      return;
    }
    this.shadowRoot.innerHTML = `
      <style>
        .product-demo{
        }
        .demo-label{
          font-size: 13px;
          margin-bottom: .6rem;
        }
        .demo-list{
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .demo-item{
          width: 100px;
          overflow: hidden;
          border-radius: 4px;
          text-decoration: none;
          border: 1px solid #ccc;
        }
        .image{
          font-size: 0px;
        }
        img{
          width: 100%;
        }
        .price{
          line-height: 28px;
          color: rgb(var(--color-foreground));
          text-decoration: none;
          text-align: center;
          font-size: 14px;
          border-top: 1px solid #ccc;
        }
        .g-image{
          position: relative;
          height: 0;
          overflow: hidden;
          width: 100%;
          padding-top: var(--aspect-ratio);
        }
        .g-image img{
          position: absolute;
          z-index: 1;
          top: 0px;
          left: 0px;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      </style>
      <div class="product-demo">
        <label class="demo-label">Demo</label>
        <div class="demo-list">
          ${Object.values(this.demo_products)
            .map(
              (product) => `
            <a class="demo-item" href="/products/${product.handle}" target="_blank">
              <div class="image">
                <div class="g-image" style="--aspect-ratio: ${(product.variants[0].image.height / product.variants[0].image.width) * 100}%;">
                  <img src="${product.variants[0].image.url}&width=120" alt="" />
                </div>
              </div>
              <div class="price">${theme.Currency.formatMoney(product.variants[0].price.amount, `{{amount}} ${product.variants[0].price.currencyCode}`)}</div>
            </a>  
          `
            )
            .join("")}
        </div>
      </div>
    `;
  }

  clear() {
    this.shadowRoot.innerHTML = "";
  }

  async fetchProductsByVariantsIDs(ids) {
    const query = `
      query getProductsByVariantIDs($variantIds: [ID!]!) {
        nodes(ids: $variantIds) {
          ... on ProductVariant {
            id
            sku
            title
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            image {
              url
              altText
            }
            product {
              id
              title
              handle
              description
              images(first: 5) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      // variantIds: [`gid://shopify/ProductVariant/${this.variantsSku[0].id}`]
      variantIds: ids ? ids.map((e) => `gid://shopify/ProductVariant/${e}`) : [...this.variantsSku.map((e) => `gid://shopify/ProductVariant/${e.id}`)],
    };

    console.log("fetchProductsByVariantsIDs:variables", variables);

    try {
      const response = await Shopify.client.request(query, { variables });
      console.log("fetchProductsByVariantsIDs", response);
    } catch (error) {
      console.error("Error fetching demo products:", error);
      if (error.graphQLErrors) {
        console.error("GraphQL Errors:", error.graphQLErrors);
      }
      if (error.networkError) {
        console.error("Network Error:", error.networkError);
      }
    }
  }

  async fetchVariantsByIDs(variantIds) {
    const query = `
      query getVariantsByIDs($variantIds: [ID!]!) {
        nodes(ids: $variantIds) {
          ... on ProductVariant {
            id
            sku
            title
            availableForSale
            currentlyNotInStock
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            image {
              url
              altText
            }
            selectedOptions {
              name
              value
            }
            product {
              id
              title
              handle
              description
              images(first: 5) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = variantIds
      ? {
          variantIds: Array.isArray(variantIds) ? variantIds.map((id) => `gid://shopify/ProductVariant/${id}`) : [`gid://shopify/ProductVariant/${variantIds}`],
        }
      : {
          variantIds: this.variantsSku.map((e) => `gid://shopify/ProductVariant/${e.id}`),
        };

    try {
      const response = await Shopify.client.request(query, { variables });
      console.log("Fetched variants:", response);
      if (response?.data?.nodes) {
        const formattedVariants = response.data.nodes
          .map((node) => {
            if (node) {
              return {
                ...node,
                price: {
                  ...node.price,
                  amount: parseFloat(node.price.amount).toFixed(2),
                },
                compareAtPrice: node.compareAtPrice
                  ? {
                      ...node.compareAtPrice,
                      amount: parseFloat(node.compareAtPrice.amount).toFixed(2),
                    }
                  : null,
              };
            }
            return null;
          })
          .filter((node) => node !== null);

        return formattedVariants;
      }
    } catch (error) {
      console.error("Error fetching variants:", error);
      if (error.graphQLErrors) {
        console.error("GraphQL Errors:", error.graphQLErrors);
      }
      if (error.networkError) {
        console.error("Network Error:", error.networkError);
      }
      throw error;
    }
  }

  getVariants() {
    const variants = document.querySelector("script[data-variants]")?.innerHTML;
    return !!variants ? JSON.parse(variants) : null;
  }

  // // 获取自定义属性variant-id和product-id
  // static get observedAttributes() {
  //   return ['variant-id'];
  // }

  // // 监听自定义属性variant-id变化
  // attributeChangedCallback(name, oldValue, newValue) {
  //   if(!this.demo_products) return;
  //   if (name === 'variant-id') {
  //     this.variant_id = newValue;
  //     this.updateDemoProduct();
  //   }
  // }
}
customElements.define("product-demo", ProductDemo);

class PE {
  constructor() {
    this.init();
  }

  async init () {
    await this.initClinet();

    this.fetchProducts();
  }

  async fetchProducts () {
    const query = `query ExportProductVariants($cursor: String) {
      products(
        first: 50
        after: $cursor
        query: "status:active"
      ) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            handle
            title
            variants(first: 100) {
              edges {
                node {
                  id
                  sku
                  barcode
                  price
                  compareAtPrice
                  selectedOptions {
                    name
                    value
                  }
                  metafield(namespace: "custom", key: "sku2_til_regnskab") {
                    value
                  }
                }
              }
            }
          }
        }
      }
    }`;

    try {
      const response = await Shopify.client.request(query);
      console.log("fetchProducts", response);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }

  

  async initClinet() {
    if (!Shopify.client) {
      await this.loadStorefrontAPI();
      Shopify.client = ShopifyStorefrontAPIClient.createStorefrontApiClient({
        storeDomain: Shopify.shop,
        apiVersion: "2025-01",
        publicAccessToken: "78dbef5026ec011a0c50d6db67199f50",
      });
      return Promise.resolve();
    }
    return Promise.resolve();
  }

  loadStorefrontAPI() {
    return new Promise((resolve, reject) => {
      if (window.ShopifyStorefrontAPIClient) {
        resolve();
      } else {
        const script = document.createElement("script");
        script.src = storefront_api_client_url;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      }
    });
  }
}

customElements.define(
  "product-demo-preview",
  class extends ClinetHTMLElement {
    constructor() {
      super();
      this.isInitialized = false;
      this.variant_id = this.getAttribute("variant-id");
      this.render();
    }

    connectedCallback() {}

    render() {
      this.fetchVariantDemo();
      this.isInitialized = true;
    }

    fetchVariantDemo() {
      if (!this.variant_id) return;

      let rootUrl = this.dataset.rootUrl || "/";
      if (!rootUrl.endsWith("/")) {
        rootUrl = rootUrl + "/";
      }
      const variantSectionUrl = `${rootUrl}variants/${this.variant_id}/?section_id=product-demo-preview`;

      fetch(variantSectionUrl)
        .then((response) => response.text())
        .then((text) => {
          const sectionInnerHTML = new DOMParser().parseFromString(text, "text/html").querySelector(".shopify-section");
          this.renderDemo(sectionInnerHTML);
        })
        .catch((e) => {
          console.log(e);
          this.innerHTML = "";
        });
    }

    renderDemo(section) {
      const product_demo_preview = section.querySelector("product-demo-preview");
      if (product_demo_preview) {
        this.innerHTML = product_demo_preview.innerHTML;
      }
    }

    // 获取自定义属性variant-id和product-id
    static get observedAttributes() {
      return ["variant-id"];
    }

    // 监听自定义属性variant-id变化
    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "variant-id" && this.isInitialized) {
        this.variant_id = newValue;
        this.fetchVariantDemo();
      }
    }
  }
);

customElements.define(
  "inventory-info",
  class extends ClinetHTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this.render();
      this.variants_inventory = null;
      this.variant_info = "";
    }
    async connectedCallback() {
      if (!this.getAttribute("product-id")) return;
      this.product_id = `gid://shopify/Product/${this.getAttribute("product-id")}`;
      this.variant_id = `gid://shopify/ProductVariant/${this.getAttribute("variant-id")}`;

      await this.initClinet();
      this.fetchInventory();
    }

    async fetchInventory() {
      const query = `
      query getVariantData($productId: ID!) {
        product(id: $productId) {
          id
          title
          handle
          variants(first: 10) {
            edges {
              node {
                id
                title
                quantityAvailable
                storeAvailability(first: 20) {
                  edges {
                    node {
                      available
                      quantityAvailable
                      pickUpTime
                      location {
                        id
                        name
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

      const variables = {
        productId: this.product_id,
      };

      try {
        const response = await Shopify.client.request(query, { variables });
        if (response?.data?.product) {
          this.variants_inventory = {};
          response.data.product.variants.edges.forEach(({ node: item }) => {
            const { quantityAvailable } = item;
            const currentStoreAvailability = item.storeAvailability.edges.reduce((res, { node: cur }) => (res += cur.quantityAvailable), 0);
            this.variants_inventory[item.id] = {
              id: item.id,
              quantityAvailable,
              currentStoreAvailability,
              otherStoreAvailability: quantityAvailable - currentStoreAvailability,
            };
          });

          publish("update-variants-inventory", {
            data: {
              variantsInventory: this.variants_inventory,
            },
          });

          this.updateInventoryInfo();
          if (!this.hasAttribute("updated")) this.checkCurrentVariantInventory();
        } else {
          console.error("[sales channels have no access]Error fetching products, response:", response);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    }

    checkCurrentVariantInventory() {
      const { currentStoreAvailability: denmark_inventory, otherStoreAvailability: germany_inventory } = this.variants_inventory[this.variant_id];
      if (denmark_inventory <= 0 && germany_inventory <= 0) {
        // 如果当前默认变体的德国&丹麦库存都为0，则强制选中首个库存不为0的变体
        const firstAvailableVariant = Object.values(this.variants_inventory).find((item) => item.currentStoreAvailability > 0 || item.otherStoreAvailability > 0);
        if (firstAvailableVariant) {
          publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event: { target: this },
              variantId: firstAvailableVariant.id.replace("gid://shopify/ProductVariant/", ""),
            },
          });
        }
      }
      // if(denmark_inventory <= 0){
      //   document.querySelector("pickup-availability").remove();
      // }
    }

    updateInventoryInfo() {
      // 根据不同的库存信息更新variant_info, currentStoreAvailability为denmark库存 otherStoreAvailability为germany库存
      const { currentStoreAvailability: denmark_inventory, otherStoreAvailability: germany_inventory } = this.variants_inventory[this.variant_id];
      // 根据注释更新文案
      // 1)
      // In stock in Denmark
      // In stock in Germany (
      // 1-2 days delivery time. Pickup possible.

      // 2)
      // In stock in Denmark
      // Not in stock in Germany (
      // 1-2 days delivery time. Pickup possible.

      // 3)
      // Not in stock in Denmark
      // In stock in Germany
      // 3-5 days delivery time.

      // 4)
      // Not in stock in Denmark
      // Not in stock in Germany
      // 21+ days delivery time.
      // console.log("根据变体ID更新库存信息:", this.variant_id, "丹麦库存：", denmark_inventory, "德国库存:", germany_inventory);

      if (denmark_inventory > 0) {
        this.classList.remove("pickup-availability-hide");
      } else {
        this.classList.add("pickup-availability-hide");
      }

      if (denmark_inventory > 0 && germany_inventory > 0) this.variant_info = theme.strings.inventory_text_1;
      if (denmark_inventory > 0 && germany_inventory <= 0) this.variant_info = theme.strings.inventory_text_2;
      if (denmark_inventory <= 0 && germany_inventory > 0) this.variant_info = theme.strings.inventory_text_3;
      if (denmark_inventory <= 0 && germany_inventory <= 0) this.variant_info = theme.strings.inventory_text_4;
      this.updateContent(this.variant_info, !!(denmark_inventory <= 0 && germany_inventory <= 0));
    }

    render() {
      this.shadowRoot.innerHTML = `<slot></slot>`;
    }

    updateContent(text, out_of_stock) {
      this.shadowRoot.innerHTML = `
      <style>
        @keyframes inventory-pulse {
          0% {
            opacity: 0.5;
          }
          100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }
        .stock{
          display: flex;
          gap: 15px;
          align-items: center;
        }
        .status{
          display: inline-block;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--bg-color);
        }
        .in-stock{
          --bg-color: #61C104;
        }
        .out-of-stock{
          --bg-color: #f4af29;
        }
        .status:before{
          content: "";
          display: block;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--bg-color);
          animation: inventory-pulse 2s linear infinite;
        }
      </style>
      <div class="stock">
        <span class="status ${out_of_stock ? "out-of-stock" : "in-stock"}"></span>
        <span>${text}</span>
      </div>
    `;
    }

    // 获取自定义属性variant-id和product-id
    static get observedAttributes() {
      return ["variant-id"];
    }

    // 监听自定义属性variant-id变化
    attributeChangedCallback(name, oldValue, newValue) {
      if (!this.variants_inventory) return;
      if (name === "variant-id") {
        this.variant_id = `gid://shopify/ProductVariant/${newValue}`;
        this.updateInventoryInfo();
      }
    }
  }
);

var SnowScreen = class extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.snow_image = this.getAttribute("snow-image");
  }
};
if (!window.customElements.get("snow-screen")) {
  window.customElements.define("snow-screen", SnowScreen);
}

customElements.get("g-drawer") ||
  customElements.define(
    "g-drawer",
    class extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
        this.querySelector(".drawer__overlay").addEventListener("click", this.close.bind(this));
        this.querySelector(".drawer__close").addEventListener("click", this.close.bind(this));
      }

      open() {
        setTimeout(() => {
          this.classList.add("animate", "active");
        });
        document.body.classList.add("overflow-hidden");
      }

      close() {
        this.classList.remove("active");
        document.body.classList.remove("overflow-hidden");
      }
    }
  );

Shopify.Drawer = class {
  constructor({ el, classes = "", theme = "light", styles = "" }) {
    this.uid = `drawer-${Math.floor(Math.random() * 100000)}`;
    this.renderDrawer(el, classes, theme, styles);
  }

  renderDrawer(el, classes, theme, styles) {
    this.drawer = new DOMParser()
      .parseFromString(
        `
      <g-drawer id="${this.uid}" class="drawer${classes ? " " + classes : ""} ${theme}">
        <div>
          <div class="drawer__overlay"></div>
          <div class="drawer__inner gradient color-scheme-1" style="${styles}">
            <button class="drawer__close" type="button" aria-label="Luk">
              <span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"></path></svg></span>
            </button>
            <div class="drawer__content">
              
            </div>
          </div>
        </div>
      </g-drawer>  
    `,
        "text/html"
      )
      .querySelector("g-drawer");
    if (el instanceof HTMLElement) {
      this.drawer.querySelector(".drawer__content").appendChild(el);
    } else {
      this.drawer.querySelector(".drawer__content").innerHTML = el;
    }
    document.body.appendChild(this.drawer);
  }

  open() {
    this.drawer.open();
  }

  close() {
    this.drawer.close();
  }
};

customElements.get("gravitation-text") ||
  customElements.define(
    "gravitation-text",
    class extends HTMLElement {
      constructor() {
        super();
        this.attachShadow({ mode: "open" });
        this.range = this.getAttribute("range") || 2;
        this.level = this.getAttribute("level") || 25;
      }

      connectedCallback() {
        this.render();
        this.addEventListeners();
      }

      render() {
        const text = this.textContent;
        this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-block;
          position: relative;
          cursor: pointer;
        }
        span {
          display: inline-block;
          transition: transform 0.2s ease;
        }
      </style>
      <div>${text
        .split("")
        .map((char) => `<span>${char}</span>`)
        .join("")}</div>
    `;
        this.chars = this.shadowRoot.querySelectorAll("span");
      }

      addEventListeners() {
        this.addEventListener("mousemove", this.onMouseMove);
        this.addEventListener("mouseleave", this.onMouseLeave);
      }

      onMouseMove(event) {
        const rect = this.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const charWidth = rect.width / this.chars.length;
        const mouseIndex = Math.floor(x / charWidth);

        const affectedRange = this.range; // 影响鼠标两侧各2个字符，总共5个字符

        this.chars.forEach((char, index) => {
          const distance = Math.abs(index - mouseIndex);
          if (distance <= affectedRange) {
            const lift = 1 - distance / (affectedRange + 1);
            const yOffset = lift * -this.level; // 最大上升高度为20像素
            char.style.transform = `translateY(${yOffset}px)`;
          } else {
            char.style.transform = "translateY(0)";
          }
        });
      }

      onMouseLeave() {
        this.chars.forEach((char) => {
          char.style.transform = "translateY(0)";
        });
      }
    }
  );

/*====================================

use:
  mode: 
    -cover：覆盖当前区域渲染加载动画
    -more：当前区域下方追加渲染加载动画
  slot：
    -icon: 替换默认的icon  
  <g-spinning loading="true" mode="more">
    <div class="center">
      <p>111111111111111111111111111111</p>
      <p>222222222222222222222222222222</p>
      <p>333333333333333333333333333333</p>
      <p>444444444444444444444444444444</p>
    </div>
    <svg slot="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="48" height="48"><path d="M515.5 979.9c-19.33 0-35-15.67-35-35V782.8c0-19.33 15.67-35 35-35s35 15.67 35 35v162.1c0 19.33-15.67 35-35 35zM515.5 294c-19.33 0-35-15.67-35-35V96.9c0-19.33 15.67-35 35-35s35 15.67 35 35V259c0 19.33-15.67 35-35 35zM815.31 855.71c-8.96 0-17.91-3.42-24.75-10.25L675.94 730.84c-13.67-13.67-13.67-35.83 0-49.5 13.67-13.67 35.83-13.67 49.5 0l114.62 114.62c13.67 13.67 13.67 35.83 0 49.5a34.915 34.915 0 0 1-24.75 10.25zM330.31 370.71c-8.96 0-17.91-3.42-24.75-10.25L190.94 245.84c-13.67-13.67-13.67-35.83 0-49.5 13.67-13.67 35.83-13.67 49.5 0l114.62 114.62c13.67 13.67 13.67 35.83 0 49.5a34.915 34.915 0 0 1-24.75 10.25zM939.5 555.9H777.4c-19.33 0-35-15.67-35-35s15.67-35 35-35h162.1c19.33 0 35 15.67 35 35s-15.67 35-35 35zM253.6 555.9H91.5c-19.33 0-35-15.67-35-35s15.67-35 35-35h162.1c19.33 0 35 15.67 35 35s-15.67 35-35 35zM700.69 370.71c-8.96 0-17.91-3.42-24.75-10.25-13.67-13.67-13.67-35.83 0-49.5l114.62-114.62c13.67-13.67 35.83-13.67 49.5 0 13.67 13.67 13.67 35.83 0 49.5L725.44 360.46c-6.83 6.83-15.79 10.25-24.75 10.25zM215.69 855.71c-8.96 0-17.91-3.42-24.75-10.25-13.67-13.67-13.67-35.83 0-49.5l114.62-114.62c13.67-13.67 35.83-13.67 49.5 0s13.67 35.83 0 49.5L240.44 845.46a34.915 34.915 0 0 1-24.75 10.25z" fill="#8a8a8a"></path></svg>
  </g-spinning>

  tips: 如果使用了icon插槽那内容也需要使用插槽， 否则内容更新时icon插槽将会被清除
  <g-spinning loading="true" mode="more">
    <div slot="content" class="center">
      <p>111111111111111111111111111111</p>
      <p>222222222222222222222222222222</p>
      <p>333333333333333333333333333333</p>
      <p>444444444444444444444444444444</p>
    </div>
    <svg slot="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="48" height="48"><path d="M515.5 979.9c-19.33 0-35-15.67-35-35V782.8c0-19.33 15.67-35 35-35s35 15.67 35 35v162.1c0 19.33-15.67 35-35 35zM515.5 294c-19.33 0-35-15.67-35-35V96.9c0-19.33 15.67-35 35-35s35 15.67 35 35V259c0 19.33-15.67 35-35 35zM815.31 855.71c-8.96 0-17.91-3.42-24.75-10.25L675.94 730.84c-13.67-13.67-13.67-35.83 0-49.5 13.67-13.67 35.83-13.67 49.5 0l114.62 114.62c13.67 13.67 13.67 35.83 0 49.5a34.915 34.915 0 0 1-24.75 10.25zM330.31 370.71c-8.96 0-17.91-3.42-24.75-10.25L190.94 245.84c-13.67-13.67-13.67-35.83 0-49.5 13.67-13.67 35.83-13.67 49.5 0l114.62 114.62c13.67 13.67 13.67 35.83 0 49.5a34.915 34.915 0 0 1-24.75 10.25zM939.5 555.9H777.4c-19.33 0-35-15.67-35-35s15.67-35 35-35h162.1c19.33 0 35 15.67 35 35s-15.67 35-35 35zM253.6 555.9H91.5c-19.33 0-35-15.67-35-35s15.67-35 35-35h162.1c19.33 0 35 15.67 35 35s-15.67 35-35 35zM700.69 370.71c-8.96 0-17.91-3.42-24.75-10.25-13.67-13.67-13.67-35.83 0-49.5l114.62-114.62c13.67-13.67 35.83-13.67 49.5 0 13.67 13.67 13.67 35.83 0 49.5L725.44 360.46c-6.83 6.83-15.79 10.25-24.75 10.25zM215.69 855.71c-8.96 0-17.91-3.42-24.75-10.25-13.67-13.67-13.67-35.83 0-49.5l114.62-114.62c13.67-13.67 35.83-13.67 49.5 0s13.67 35.83 0 49.5L240.44 845.46a34.915 34.915 0 0 1-24.75 10.25z" fill="#8a8a8a"></path></svg>
  </g-spinning>

  document.querySelector("g-spinning").start(); // 开始加载动画
  document.querySelector("g-spinning").stop(); // 停止加载动画
  // 调用更新方法更新内容
  document.querySelector("g-spinning").update("<p>555555555555555</p>");
  // 调用更新方法更新内容并传入回调函数，回调函数中可以调用close方法关闭加载
  document.querySelector("g-spinning").update("<p>666666666666666</p>", function(close){
    close(); 
  });
  // 调用load方法并传入加载函数，执行加载函数前会自动调用start方法开始加载动画，
  // 加载函数中可以调用update方法更新内容并传入回调函数，回调函数中可以调用close方法关闭加载动画
  document.querySelector("g-spinning").load(function(update){
    setTimeout(() => {
        update("<p>666666666666666</p>", close => close());
    }, 3000);
  });

====================================*/

customElements.get("g-spinning") ||
  customElements.define(
    "g-spinning",
    class extends HTMLElement {
      constructor() {
        super();
        this.attachShadow({ mode: "open" });

        this.config = {
          size: this.getAttribute("size") || "48px",
          color: this.getAttribute("color") || "255, 255, 255",
        };
        this.mode = this.getAttribute("mode") || "cover";
        this.render();
      }

      connectedCallback() {}

      render() {
        this.shadowRoot.innerHTML = `
      <style>
        :root{
          --spinning-size: ${this.config.size};
          --dialog-color: ${this.config.color};
        }
        .loading__wrapper{
          position: absolute;
          z-index: 99;
          top: 0px;
          left: 0px;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(var(--dialog-color, 255, 255, 255), 0.7);
        }
        .loading__more{
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @keyframes spinning-loading {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .loading__inner{
          font-size: 0px;
          animation: spinning-loading 1.2s linear infinite;
        }
        .loading__icon{
          display: block !important;
          width: var(--spinning-size, 48px);
          height: var(--spinning-size, 48px);
          background: url(https://cdn.shopify.com/s/files/1/0647/5923/4815/files/2.png?v=1736906489) no-repeat center center / 100%;
        }
      </style>
      <slot></slot>
      <slot name="content"></slot>
      <div class="loading__wrapper">
        <div class="loading__inner">
          <slot name="icon">
            <div class="loading__icon"></div>
          </slot>
        </div>
      </div>
    `;
      }

      createLoading() {
        // const loading = document.createElement("div");
        // if(this.mode == "cover")
        //   loading.classList.add("loading__wrapper");
        // else if(this.mode == "more")
        //   loading.classList.add("loading__more");
        // if(this.config.size)
        //   loading.style.setProperty(`--spinning-size`, this.config.size);
        // if(this.config.color)
        //   loading.style.setProperty(`--dialog-color`, this.config.color);

        // const inner = document.createElement("div");
        // inner.classList.add("loading__inner");

        // let icon = this.querySelector("[slot='icon']");
        // if(icon){
        //   icon.removeAttribute("slot");
        //   inner.appendChild(icon);
        // }else{
        //   inner.innerHTML = `<div class="loading__icon"></div>`;
        // }

        // loading.appendChild(inner);
        // return loading;
        const loading = this.shadowRoot.querySelector(".loading__wrapper");
        this.loading = loading;
        this.shadowRoot.removeChild(loading);
      }

      start() {
        if (!this.loading) this.createLoading();

        this.shadowRoot.appendChild(this.loading);
        // this.appendChild(this.loading);
      }

      stop() {
        this.loading?.remove();
      }

      load(fn) {
        this.start();
        if (typeof callback === "function") fn(this.update.bind(this));
      }

      update(content, callback) {
        const contentSlot = this.querySelector("[slot='content']");
        this.replaceContent(contentSlot || this, content);
        if (typeof callback === "function") callback(this.stop.bind(this));
      }

      replaceContent(target, content) {
        if (!target) return;
        if (content instanceof Element) target.appendChild(content);
        else if (typeof content === "string") target.innerHTML = content;
      }

      updateLoadStatus(status) {
        status ? this.start() : this.stop();
      }

      static get observedAttributes() {
        return ["loading"];
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name == "loading" && oldValue !== newValue) {
          this.updateLoadStatus(newValue == "true");
        }
      }
    }
  );

customElements.define(
  "countdown-timer",
  class extends HTMLElement {
    constructor() {
      super();

      this.attachShadow({ mode: "open" });
      this.hide_unit = this.getAttribute("unit") == "hide";
      this.size = this.getAttribute("size") || "normal";
      this.start = new Date(this.dataset.start).getTime();
      this.end = new Date(this.dataset.end).getTime();

      this.controller = {
        days: {
          number: 0,
          cards: [],
        },
        hours: {
          number: 0,
          cards: [],
        },
        minutes: {
          number: 0,
          cards: [],
        },
        seconds: {
          number: 0,
          cards: [],
        },
      };

      if (this.dataset.start && this.start > Date.now()) {
        this.style.display = "none";
        // 设置一个定时器在开始时间到达时显示
        setTimeout(() => {
          this.style.display = "";
          this.init();
        }, this.start - Date.now());
        return;
      }

      this.styleCss = {
        backgroundColor: this.dataset.backgroundColor || "#6e6b4b",
        cardColor: this.dataset.cardColor || "#000",
        textColor: this.dataset.textColor || "#fff",
      };

      this.init();
    }

    init() {
      // 初始化HTML结构
      const styles = `
      <style>
        :host{
          ${
            this.size == "small"
              ? `
              --card-width: 18px;
              --card-height: 30px;
              --font-size: 20px;
            ` : this.size == "large" ? `
              --card-width: 48px;
              --card-height: 74px;
              --font-size: 46px;
            `
              : `
              --card-width: 26px;
              --card-height: 38px;
              --font-size: 26px;
            `
          }
          --background-color: ${this.styleCss.backgroundColor};
          --card-color: ${this.styleCss.cardColor};
          --text-color: ${this.styleCss.textColor};
        }
        .countdown-wrapper {
          display: flex;
          gap: ${this.size == "large" ? 18 : 10}px;
          justify-content: center;
        }
        .countdown-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        .flip-deck {
          display: flex;
          gap: ${this.size == "large" ? 6 : 2}px;
        }
        .flip-card {
          width: var(--card-width);
          height: var(--card-height); 
          color: var(--text-color, #fff);
          box-sizing: border-box;
          text-align: center;
          font-size: var(--font-size);
          font-weight: bold;
          line-height: var(--card-height);
        }
        .flip-card-wrapper{
          position: relative;
          height: calc(var(--card-height) / 2); 
        }
        .top{
          position: relative;
        }
        .bottom{
          line-height: 0;
        }
        .block{
          width: 100%;
          height: 100%;
          background-color: var(--card-color);
          overflow: hidden;
          border-radius: 3px 3px 0 0;
          transform: perspective(var(--card-height)) rotateX(0deg);
          transform-origin: bottom;
          transform-style: preserve-3d;
        }
        .block:last-child{
          border-radius: 0 0 3px 3px;
        }
        .card-box{
          position: absolute;
          z-index: 2;
          top: 0;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          transform-origin: bottom; 
          transition: all 0.5s;
        }
        .card-block{
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility:hidden;
          overflow: hidden;
          transform: perspective(var(--card-height)) rotateX(0deg);
        }
        .front {
          border-radius: 3px 3px 0 0;
          background-color: var(--card-color);
        }
        .back {
          border-radius: 0 0 3px 3px;
          background-color: var(--card-color);
          transform: rotateX(0deg) rotateY(-180deg) scale(-1);
          line-height: 0px;
        }
        .label {
          font-size: 14px;
          color: #fff;
        }
        @media screen and (max-width: 768px) {
          .countdown-wrapper {
            gap: ${this.size == "large" ? 10 : 8}px;
          }
          .flip-deck{
            gap: ${this.size == "large" ? 2 : 6}px;
          }
          .flip-card {
            --card-width: ${this.size == "large" ? 34 : 22}px;
            --card-height: ${this.size == "large" ? 55 : 50}px;
            font-size: ${this.size == "large" ? 32 : 22}px;
          }
        }
      </style>
      `;
      const labels = {
        days: "Days",
        hours: "Hours",
        minutes: "Mins",
        seconds: "Secs",
      };
      this.shadowRoot.innerHTML = `
      ${styles}
      <div class="countdown-wrapper">
          ${Object.keys(labels)
            .map((unit) => {
              return `
                <div class="countdown-item ${this.size}">
                  <div class="flip-deck" data-unit="${unit}">
                    <div class="flip-card">
                      <div class="flip-card-wrapper">
                        <div class="top block">0</div>
                        <div class="card-box">
                          <div class="card-block front">0</div>
                          <div class="card-block back">0</div>
                        </div>
                        <div class="bottom block">0</div>
                      </div>
                    </div>
                    <div class="flip-card">
                      <div class="flip-card-wrapper">
                        <div class="top block">0</div>
                        <div class="card-box">
                          <div class="card-block front">0</div>
                          <div class="card-block back">0</div>
                        </div>
                        <div class="bottom block">0</div>
                      </div>
                    </div>
                  </div>
                  ${this.hide_unit ? "" : `<span class="label">${labels[unit]}</span>`}
                </div>
              `;
            })
            .join("")}
      </div>
    `;

      this.flipDeck = {
        days: this.shadowRoot.querySelector('[data-unit="days"]'),
        hours: this.shadowRoot.querySelector('[data-unit="hours"]'),
        minutes: this.shadowRoot.querySelector('[data-unit="minutes"]'),
        seconds: this.shadowRoot.querySelector('[data-unit="seconds"]'),
      };
    }

    connectedCallback() {
      this.updateTimer(true);
      this.timer = setInterval(() => this.updateTimer(), 10);
      // requestAnimationFrame(this.updateTimer.bind(this));
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    updateTimer(init = false) {
      const now = Date.now();
      const timeLeft = Math.max(0, this.end - now);

      if (timeLeft === 0) {
        clearInterval(this.timer);
        this.dispatchEvent(new CustomEvent("countdown-end"));
        this.hide();
        return;
      }

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

      if (init) {
        this.initDateDom(days, hours, minutes, seconds);
        return;
      }

      this.updateFlipCard("days", days);
      this.updateFlipCard("hours", hours);
      this.updateFlipCard("minutes", minutes);
      this.updateFlipCard("seconds", seconds);
    }

    hide() {
      this.style.display = "none";
    }

    initDateDom(days, hours, minutes, seconds) {
      const arr = ["days", "hours", "minutes", "seconds"];
      this.controller.days.number = String(days).padStart(2, "0").split("");
      this.controller.hours.number = String(hours).padStart(2, "0").split("");
      this.controller.minutes.number = String(minutes).padStart(2, "0").split("");
      this.controller.seconds.number = String(seconds).padStart(2, "0").split("");
      arr.forEach((key, index) => {
        const current = String(arguments[index]).padStart(2, "0");
        const next = arguments[index] - 1 < 0 ? this.getNextNumber(key) : String(arguments[index] + 1).padStart(2, "0");
        this.flipDeck[key].innerHTML = `
        <div class="flip-card">
          <div class="flip-card-wrapper">
            <div class="top block">${next.charAt(0)}</div>
            <div class="card-box">
              <div class="card-block front">${current.charAt(0)}</div>
              <div class="card-block back">${next.charAt(0)}</div>
            </div>
            <div class="bottom block">${current.charAt(0)}</div>
          </div>
        </div>
        <div class="flip-card">
          <div class="flip-card-wrapper">
            <div class="top block">${next.charAt(1)}</div>
            <div class="card-box">
              <div class="card-block front">${current.charAt(1)}</div>
              <div class="card-block back">${next.charAt(1)}</div>
            </div>
            <div class="bottom block">${current.charAt(1)}</div>
          </div>
        </div>
      `;
        this.flipDeck[key].querySelectorAll(".flip-card").forEach((card, index) => {
          this.controller[key].cards[index] = {
            box: card.querySelector(".card-box"),
            top: card.querySelector(".top"),
            back: card.querySelector(".back"),
            front: card.querySelector(".front"),
            bottom: card.querySelector(".bottom"),
          };
        });
      });
    }

    getNextNumber(unit) {
      switch (unit) {
        case "days":
          return "364";
        case "hours":
          return "23";
        case "minutes":
          return "59";
        case "seconds":
          return "59";
      }
    }

    updateFlipCard(unit, value) {
      const cell = this.controller[unit];
      const oldValue = JSON.parse(JSON.stringify(cell.number));
      const newValue = String(value).padStart(2, "0").split("");

      for (let i = 0; i < newValue.length; i++) {
        if (oldValue[i] !== newValue[i]) {
          this.controller[unit].number[i] = newValue[i];
          this.renderCard(cell.cards[i], newValue[i], oldValue[i]);
        }
      }
    }

    renderCard(card, newValue, oldValue) {
      const { box, top, back, bottom, front } = card;

      // 更新静态数字
      front.textContent = oldValue;
      bottom.textContent = oldValue;
      box.style.transition = "transform 0s";
      box.style.transform = "perspective(var(--card-height)) rotateX(0deg)";

      let timer = setTimeout(() => {
        box.style.transition = "transform 0.9s";
        back.textContent = newValue;
        top.textContent = newValue;
        box.style.transform = "perspective(var(--card-height)) rotateX(-180deg)";
        clearTimeout(timer);
      }, 100);
    }
  }
);

customElements.define(
  "text-copy",
  class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.addEventListener("click", () => {
        const text = document.querySelector(this.getAttribute("target"))?.textContent || "";
        if (text) this.copy(text);
      });
    }

    copy(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.popup();
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
        });
    }

    popup() {
      const popup = document.createElement("div");
      popup.textContent = "Copied!";
      popup.style.position = "absolute";
      popup.style.top = "10px";
      popup.style.left = "50%";
      popup.style.fontSize = "14px";
      popup.style.opacity = 0;
      popup.style.color = "#000";
      popup.style.transition = "opacity 0.5s, top 0.5s";
      popup.style.transform = "translate(-50%, -100%)";
      this.appendChild(popup);
      setTimeout(() => {
        popup.style.top = "0px";
        popup.style.opacity = 1;
      }, 10);

      setTimeout(() => {
        popup.remove();
      }, 3000);
    }
  }
);
window.googleCallback = function () {
  dispatchEvent(new CustomEvent("google-map-loaded"));
};
customElements.define(
  "google-map",
  class GoogleMap extends HTMLElement {
    static key = "AIzaSyAD9JgjqxvgY9TYHpb5IpM183NF0PT2iEA";
    constructor() {
      super();
      // 55.660162816893084, 12.293859338633487
      document.addEventListener("google-map-loaded", () => {
        console.log("google-map-loaded");
      });
    }
    connectedCallback() {
      this.url = `https://maps.googleapis.com/maps/api/js?key=${GoogleMap.key}&callback=googleCallback&language=en`;
      console.log(this.url);
      if (this.dataset.location) {
        const [lat, lng] = this.dataset.location.split(",");
        this.location = { lat: Number(lat), lng: Number(lng) };
      }
      this.address = this.dataset.address;

      this.init();
    }

    init() {
      setTimeout(() => {
        this.loadScript().then(() => {
          if (this.location) this.initMapByLocation();
          else if (this.address) this.initMapByAddress();
        });
      }, 100);
    }

    loadScript() {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = this.url;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    initMapByLocation() {
      // 你可以在 https://mapstyle.withgoogle.com/  上自定义样式
      console.log(this.location);
      const svgIcon = {
        url:
          "data:image/svg+xml;charset=UTF-8," +
          encodeURIComponent(`
          <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" width="32" height="32">
            <path d="M128 417.15712C128 204.32896 286.70976 30.72 501.64736 30.72c218.97216 0 395.99104 166.57408 395.99104 386.43712 0 313.18528-407.168 579.70688-395.99104 579.70688C512.8192 996.864 128 726.28736 128 417.15712z m381.83424 178.51392c113.0496 0 204.69248-91.648 204.69248-204.6976 0-113.04448-91.648-204.68736-204.6976-204.68736-113.04448 0-204.68736 91.64288-204.68736 204.69248s91.648 204.69248 204.69248 204.69248z" fill="#6e6b4b"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(32, 32), // 大小可调
      };
      const map = new google.maps.Map(this, {
        zoom: 15,
        center: this.location,
        // styles: [
        //   {
        //     featureType: "administrative.land_parcel",
        //     stylers: [
        //       {
        //         visibility: "off",
        //       },
        //     ],
        //   },
        //   {
        //     featureType: "administrative.neighborhood",
        //     stylers: [
        //       {
        //         visibility: "off",
        //       },
        //     ],
        //   },
        //   {
        //     featureType: "poi",
        //     elementType: "labels.text",
        //     stylers: [
        //       {
        //         visibility: "off",
        //       },
        //     ],
        //   },
        //   {
        //     featureType: "road",
        //     elementType: "geometry.fill",
        //     stylers: [
        //       {
        //         color: "#6e6b4b",
        //       },
        //       {
        //         saturation: 100,
        //       },
        //     ],
        //   },
        //   {
        //     featureType: "road",
        //     elementType: "labels",
        //     stylers: [
        //       {
        //         visibility: "off",
        //       },
        //     ],
        //   },
        //   {
        //     featureType: "water",
        //     elementType: "labels.text",
        //     stylers: [
        //       {
        //         visibility: "off",
        //       },
        //     ],
        //   },
        // ],
      });

      new google.maps.Marker({
        position: this.location,
        map: map,
        icon: svgIcon,
      });
    }
  }
);


/* 
  Demo:
  <common-swiper>
    <script type="application/json" data-swiper-config>
      {
        "slidesPerView": 1.4,
        "spaceBetween": 16,
        "ResizeObserverSize": true,
        "breakpoints": {
          "768": {
            "slidesPerView": 2.4,
            "spaceBetween": 20
          }
        }
      }
    </script>
    <div class="swiper">
      <div class="swiper-wrapper">
        <div class="swiper-slide"></div>
        <div class="swiper-slide"></div>
      </div>
    </div>
  </common-swiper> 
*/
const swiperEvent = {
  initOffset: function (commonSwiper) {
    if (window.innerWidth < 768) return;
    const resizeHandle = function () {
      const section = commonSwiper.closest(".shopify-section");
      const offset = section
        .querySelector(".section-heading")
        .getBoundingClientRect().left;
      const innerWidth = section
        .querySelector(".section-heading")
        .getBoundingClientRect().width;
      commonSwiper.style.setProperty("--slide-width", innerWidth + "px");
      commonSwiper.container.style.setProperty(
        "--swiper-offset",
        offset + "px",
      );
      commonSwiper.config.navigation.nextEl.style.right = offset + 40 + "px";
      commonSwiper.config.navigation.prevEl.style.left = offset + 40 + "px";
    };

    resizeHandle();

    window.addEventListener("resize", () => {
      if (window.innerWidth < 768) return;
      resizeHandle();
    });

    return () => {
      window.removeEventListener("resize", resizeHandle);
    };
  },
  initOffset2: function (commonSwiper) {
    const resizeHandle = function () {
      const section = commonSwiper.closest(".shopify-section");
      const offset = section
        .querySelector(".section-heading")
        .getBoundingClientRect().left;
      const innerWidth = section
        .querySelector(".section-heading")
        .getBoundingClientRect().width;
      if (window.innerWidth < 768) {
        commonSwiper.style.setProperty("--slide-width", innerWidth + "px");
      } else {
        commonSwiper.style.setProperty(
          "--slide-width",
          innerWidth * 0.5 - 20 + "px",
        );
      }
      commonSwiper.container.style.setProperty(
        "--swiper-offset",
        offset + "px",
      );
      commonSwiper.config.navigation.nextEl.style.right = offset + 40 + "px";
      commonSwiper.config.navigation.prevEl.style.left = offset + 40 + "px";
    };

    resizeHandle();

    window.addEventListener("resize", () => {
      if (window.innerWidth < 768) return;
      resizeHandle();
    });

    return () => {
      window.removeEventListener("resize", resizeHandle);
    };
  },
  alignmentButton: function (commonSwiper) {
    const resizeHandle = function () {
      const section = commonSwiper.closest(".shopify-section");
      const offset = section
        .querySelector(".section-heading")
        .getBoundingClientRect().left;
      commonSwiper.config.navigation.nextEl.style.right = offset + "px";
      commonSwiper.config.navigation.prevEl.style.left = offset + "px";
    };

    resizeHandle();

    window.addEventListener("resize", () => {
      resizeHandle();
    });

    return () => {
      window.removeEventListener("resize", resizeHandle);
    };
  },
  "scroll-text": function ({ activeIndex }) {
    if (!this.textList) this.textList = this.querySelectorAll(".text-item");
    this.textList.forEach((item, index) => {
      if (index === activeIndex) {
        item.classList.add("active");
      } else {
        item.classList.remove("active");
      }
    });
  },
  "text-card-control": function (swiper) {
    if (!this.textList) this.textList = this.querySelectorAll(".text-item");
    this.textList.forEach((item, index) => {
      item.addEventListener("click", () => {
        swiper.slideTo(index);
      });
    });
  },
  "switch-tab": function ({ activeIndex }) {
    if (!this.tabList) this.tabList = this.querySelectorAll(".tab-item");
    if (!this.textList) this.textList = this.querySelectorAll(".text-item");
    if (!this.descriptionList)
      this.descriptionList = this.querySelectorAll(".description-item");
    this.tabList.forEach((item, index) => {
      if (index === activeIndex) {
        item.classList.add("active");
      } else {
        item.classList.remove("active");
      }
    });
    this.descriptionList.forEach((item, index) => {
      if (index === activeIndex) {
        item.classList.add("active");
      } else {
        item.classList.remove("active");
      }
    });
    this.textList.forEach((item, index) => {
      if (index === activeIndex) {
        item.classList.add("active");
      } else {
        item.classList.remove("active");
      }
    });
  },
  "tab-control": function (swiper) {
    if (!this.tabList) this.tabList = this.querySelectorAll(".tab-item");
    if (!this.textList) this.textList = this.querySelectorAll(".text-item");
    this.tabList.forEach((item, index) => {
      item.addEventListener("click", () => {
        swiper.slideTo(index);
      });
    });
    this.textList.forEach((item, index) => {
      item.addEventListener("click", () => {
        swiper.slideTo(index);
      });
    });
  },
};

customElements.define(
  "common-swiper",
  class CommonSwiper extends HTMLElement {
    // 函数返回避免数据共享
    static config = () => ({
      slidesPerView: 1,
      spaceBetween: 10,
      breakpoints: {
        768: {
          spaceBetween: 24,
        },
        1240: {
          spaceBetween: 32,
        },
      },
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
    });

    constructor() {
      super();
      this.beforeCreateEvent = this.getBeforeCreateEvent();
      this.initCallback =
        swiperEvent[this.getAttribute("on-init")]?.bind(this) || null;
      this.only_mobile = this.hasAttribute("only-mobile");
      this.only_desktop = this.hasAttribute("only-desktop");
    }

    connectedCallback() {
      if (this.only_mobile && window.innerWidth > 768) return;
      if (this.only_desktop && window.innerWidth < 768) return;
      this.container = this.querySelector(".swiper");
      if (!this.container) return;
      if (!this.querySelectorAll(".swiper-slide")?.length) return;

      this.initConfig();
    }

    initConfig() {
      this.config = this.getConfig();
      if (this.beforeCreateEvent)
        this.finishCallback = this.beforeCreateEvent(this);
      if (this.hasAttribute("thumbnail")) {
        this.config.thumbs = {};
        const thumbnailElement = document.querySelector(
          this.getAttribute("thumbnail"),
        );
        if (
          (thumbnailElement.hasAttribute("only-mobile") &&
            window.innerWidth > 768) ||
          (thumbnailElement.hasAttribute("only-desktop") &&
            window.innerWidth < 768)
        ) {
          this.initSwiper();
          return;
        }
        if (thumbnailElement.swiper) {
          this.thumbnailSwiper = thumbnailElement.swiper;
          this.config.thumbs.swiper = thumbnailElement.swiper;
          this.initSwiper();
        } else {
          thumbnailElement.addEventListener(
            "swiper-init",
            ({ detail: swiper }) => {
              this.thumbnailSwiper = swiper;
              this.config.thumbs.swiper = swiper;
              this.initSwiper();
            },
          );
        }
      } else {
        this.initSwiper();
      }
    }

    initSwiper() {
      if (!this.config.on) this.config.on = {};
      this.config.on.init = function (swiper) {
        this.dispatchEvent(new CustomEvent("swiper-init", { detail: swiper }));
        if (this.initCallback) this.initCallback(swiper);
        if (this.config.autoplay && this.hasAttribute("mouse-stop")) {
          this.mouseHandler();
        }
      }.bind(this);
      this.swiper = new Swiper(this.container, this.config);
    }

    mouseHandler() {
      this.container.addEventListener("mouseenter", () => {
        this.swiper.autoplay.stop();
      });
      this.container.addEventListener("mouseleave", () => {
        this.swiper.autoplay.start();
      });
    }

    getConfig() {
      let config =
        JSON.parse(
          this.querySelector("script[data-swiper-config]")?.textContent || null,
        ) || null;
      config = config || CommonSwiper.config();
      if (config.navigation) {
        // 判断config.navigation.nextEl是dom对象还是字符串
        if (typeof config.navigation.nextEl === "string") {
          config.navigation.nextEl = this.querySelector(
            config.navigation.nextEl,
          );
        }
        if (typeof config.navigation.prevEl === "string") {
          config.navigation.prevEl = this.querySelector(
            config.navigation.prevEl,
          );
        }
      }
      if (config.scrollbar && config.scrollbar.el) {
        config.scrollbar.el = this.querySelector(config.scrollbar.el);
      }

      if (
        this.querySelector(".swiper-pagination") ||
        this.closest(".swiper-father")?.querySelector(".swiper-pagination")
      ) {
        config.pagination = {
          el:
            this.querySelector(".swiper-pagination") ||
            this.closest(".swiper-father").querySelector(".swiper-pagination"),
          clickable: true,
        };
      } else {
        delete config.pagination;
      }
      if (
        this.getAttribute("on-slide-change") &&
        swiperEvent[this.getAttribute("on-slide-change")]
      ) {
        config.on = {
          slideChange:
            swiperEvent[this.getAttribute("on-slide-change")].bind(this),
        };
      }
      return config;
    }

    getBeforeCreateEvent() {
      return (
        swiperEvent[this.getAttribute("before-create")]?.bind(this) || null
      );
    }

    disconnectedCallback() {
      if (this.finishCallback) this.finishCallback();
      this.finishCallback = null;
      this.swiper?.destroy();
    }
  },
);


  if(!customElements.get('product-details')){
    customElements.define('product-details', class extends HTMLElement {
      constructor(){
        super();
        this.storageKey = 'detail:status';
        this.detailStatus = JSON.parse(localStorage.getItem(this.storageKey)) || {};
        // 初始化列表属性
        this.image_list = [];
        this.file_list = [];
        this.video_list = [];

        try {
          this.getTemplateJSON();
        } catch (error) {
          console.error('Error initializing product details:', error);
        }
      }

      getTemplateJSON(){
        console.log("Template JSON");
        // 重置列表
        this.image_list = [];
        this.file_list = [];
        this.video_list = [];
        
        const templateJSON = this.querySelector('script[data-template]')?.textContent || null;
        this.templateName = this.querySelector('script[data-template]')?.dataset.template || "product.error";
        if(templateJSON){
          try {
            this.templateJSON = JSON.parse(templateJSON);
          } catch (error) {
            console.error('Error parsing template JSON:', error);
            this.templateJSON = { sections: {}, order: [] };
          }
        }

        const sectionList = this.querySelectorAll('script[data-section]');
        sectionList?.forEach((item) => {
          let sectionJSON = JSON.parse(item.textContent || null);
          if(sectionJSON?.value?.type == "section-specifications")
            sectionJSON = this.getSpecificationJSON(sectionJSON, item);
          if(sectionJSON?.value?.type == "section-video")
            this.fixVideoSetting(sectionJSON);
          this.templateJSON.sections[sectionJSON.key] = sectionJSON.value;
          this.templateJSON.order.push(sectionJSON.key);
          if(sectionJSON.image_list){
            this.image_list.push(...sectionJSON.image_list.slice(0, -1).split(',').map((item) => `${item.trim().split("?")[0]}`));
          }
          if(sectionJSON.file_list){
            this.file_list.push(...sectionJSON.file_list.slice(0, -1).split(',').map((item) => `${item.trim().split("?")[0]}`));
          }
          if(sectionJSON.video_list){
            this.video_list.push(...sectionJSON.video_list.slice(0, -1).split(',').map((item) => `${item.trim().split("?")[0]}`));
          }
        });
        this.enrichNavigation();
        console.log(this.image_list, this.file_list, this.video_list);
        console.log(this.templateJSON);
        window.cc = this.downloadJSON.bind(this);
        window.dd = this.downloadFiles.bind(this);
      }

      fixVideoSetting(sectionJSON){
        const settings = sectionJSON?.value?.settings;
        if (!settings) return;
        const video = settings.video || '';
        const bad =
          !video ||
          video.includes('VideoDrop') ||
          /\.HD-\d+p-/i.test(video) ||
          /(?:^|\/)[a-f0-9]{32}\./i.test(video);
        if (!bad) return;
        const coverFile = String(settings.video_cover || '')
          .replace(/^shopify:\/\/shop_images\//, '')
          .replace(/^files\//, '');
        if (!coverFile.includes('.Still')) return;
        const base = coverFile.split('.Still')[0].split('.')[0];
        if (!base) return;
        settings.video = `shopify://files/videos/${base.replace(/_/g, ' ')}.mp4`;
      }

      enrichNavigation(){
        if (!this.templateJSON?.sections || !this.templateJSON?.order) return;

        const NAV_TYPES = {
          'section-faq': { id: 'faqs', label: 'FAQs' },
          'section-specifications': { id: 'specs-sizes', label: 'Specifikationer' },
          'section-download': { id: 'downloads', label: 'Downloads' },
          'section-recommendations': { id: 'recommendations', label: 'anbefalinger' },
        };
        const SKIP = new Set([
          'main-product',
          'widget-product-navigation',
          'widget-product-details',
          'apps',
          'related-products',
          ...Object.keys(NAV_TYPES),
        ]);

        const { sections, order } = this.templateJSON;
        const navTabs = [];
        let overviewAssigned = false;

        for (const key of order) {
          const section = sections[key];
          if (!section?.type) continue;

          if (!overviewAssigned && !SKIP.has(section.type)) {
            section.settings = section.settings || {};
            if (!section.settings.anchor_id) section.settings.anchor_id = 'overview';
            navTabs.push({ label: 'Oversigt', target_id: 'overview' });
            overviewAssigned = true;
          }

          const nav = NAV_TYPES[section.type];
          if (nav) {
            section.settings = section.settings || {};
            if (!section.settings.anchor_id) section.settings.anchor_id = nav.id;
            if (!navTabs.some((t) => t.target_id === nav.id)) {
              navTabs.push({ label: nav.label, target_id: nav.id });
            }
          }
        }

        if (navTabs.length <= 1) return;

        const blocks = {};
        const block_order = [];
        navTabs.forEach((tab, i) => {
          const id = `tab_${i + 1}`;
          blocks[id] = { type: 'tab', settings: tab };
          block_order.push(id);
        });

        sections.widget_product_navigation = {
          type: 'widget-product-navigation',
          blocks,
          block_order,
          settings: {
            is_sticky_navigation: true,
            padding_top: 100,
            padding_bottom: 40,
            padding_top_mobile: 60,
            padding_bottom_mobile: 20,
          },
        };

        if (!order.includes('widget_product_navigation')) {
          const mainIdx = order.indexOf('main');
          order.splice(mainIdx >= 0 ? mainIdx + 1 : 0, 0, 'widget_product_navigation');
        }
      }

      downloadJSON(){
        // 下载JSON文件
        try {
          if (!this.templateJSON) {
            console.error('No template JSON available');
            return;
          }

          console.log(this.templateName, this.templateJSON);
          
          // 使用2个空格缩进使JSON更易读
          const header = `/*\n * ------------------------------------------------------------\n * IMPORTANT: The contents of this file are auto-generated.\n *\n * This file may be updated by the Shopify admin theme editor\n * or related systems. Please exercise caution as any changes\n * made to this file may be overwritten.\n * ------------------------------------------------------------\n */\n`;
          const jsonString = header + JSON.stringify(this.templateJSON, null, 2);
          const blob = new Blob([jsonString], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.templateName}.json`;
          
          // 模拟点击下载
          document.body.appendChild(a);
          a.click();
          
          // 清理：移除元素和释放URL
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }, 100);
        } catch (error) {
          console.error('Error downloading JSON file:', error);
        }
      }

      downloadFiles(){
        // 下载所有文件
        try {
          // 使用队列方式下载，避免同时触发太多下载
          const allFiles = [...this.image_list, ...this.file_list, ...this.video_list];
          
          if (allFiles.length === 0) {
            console.log('No files to download');
            return;
          }
          
          const downloadQueue = (files, index = 0) => {
            if (index >= files.length) return;
            
            const a = document.createElement('a');
            a.href = files[index];
            a.download = files[index].split('/').pop();
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
            // 延迟下载下一个文件，避免浏览器阻止多个同时下载
            setTimeout(() => downloadQueue(files, index + 1), 100);
          };
          
          downloadQueue(allFiles);
        } catch (error) {
          console.error('Error downloading files:', error);
        }
      }

      getSpecificationJSON(sectionJSON, section){
        const blocks = {};
        const block_order = [];
        const tr_list = section.closest(".widget-container").querySelectorAll('tr');
        tr_list.forEach((tr) => {
          const block_key = `specification_${Math.random().toString(36).substring(2, 8)}`;
          blocks[block_key] = {
            "type": "specification",
					  "settings": {
              "is_header": !tr.querySelector('td:nth-child(2)')?.textContent?.trim(),
              "label": tr.querySelector('td')?.textContent?.trim() || "",
              "value": tr.querySelector('td:nth-child(2)')?.innerHTML.trim() || ""
            }
          };
          block_order.push(block_key);
        });
        sectionJSON.value.blocks = blocks;
        sectionJSON.value.block_order = block_order;
        return sectionJSON;
      }

      developer(){
        const tmplBtn = document.createElement("div");
        tmplBtn.classList.add("button");
        tmplBtn.textContent = "下载模板文件";
        tmplBtn.addEventListener("click", (e) => {
          e.preventDefault();
          this.downloadJSON();
        });

        const fileBtn = document.createElement("div");
        fileBtn.classList.add("button");
        fileBtn.textContent = "下载资源文件";
        fileBtn.addEventListener("click", (e) => {
          e.preventDefault();
          this.downloadFiles();
        });

        const box = document.createElement("div");
        Object.assign(box.style, {
          "position": "fixed",
          "top": "50%",
          "right": "30px",
          "transform": "translateY(-50%)",
          "display": "flex",
          "flex-direction": "column",
          "gap": "10px"
        });
        box.appendChild(tmplBtn);
        box.appendChild(fileBtn);

        document.body.appendChild(box);
      }

      connectedCallback(){
        this.details = this.querySelectorAll('.tab-area > details');
        this.enable_sticky = this.querySelector('.product-nav').hasAttribute('sticky');
        this.mover = this.querySelector('.mover');
        this.tabs = this.querySelectorAll('.product-tab-item');
        this.tabAreas = document.querySelectorAll('.tab-area');

        this.currentTab = null;

        this.throttledSetMover = throttle2(this.setMover.bind(this), 200);

        this.initMoverEvent();
        if(this.enable_sticky){
          Shopify.productNav = {
            isOpen: false
          }
          this.initStickyEvent();
          this.initTabEvent();
          this.initDetailsStatus();
          this.addDetailsListeners();
        
          // 添加窗口大小变化的监听器
          window.addEventListener('resize', () => {
            this.throttledSetMover(this.querySelector('.tab-active') || this.querySelector('.product-tab-item'));
          });
        }
      }

      addDetailsListeners() {
        this.details.forEach((detail, index) => {
          const id = detail.id || `detail-${index}`;
          detail.addEventListener('toggle', () => {
            this.saveDetailStatus(id, detail.open);
          });
        });
      }

      saveDetailStatus(key, isOpen) {
        this.detailStatus[key] = isOpen;
        localStorage.setItem(this.storageKey, JSON.stringify(this.detailStatus));
      }
      
      initDetailsStatus(){
        document.addEventListener('DOMContentLoaded', () => {
          this.details.forEach((detail, index) => {
            if(window.innerWidth >= 768){
              detail.open = true;
            }else{
              const key = detail.id || `detail-${index}`;
              if (this.detailStatus[key] !== undefined) {
                detail.open = this.detailStatus[key];
              }
            }
          });
        });
      }
      initStickyEvent(){
        // 监听页面滚动，当nav离开屏幕可视范围时进行绝对定位到顶部
        let nav = this.querySelector('.product-nav');
        let nav_height = nav.offsetHeight;
        this.querySelector(".product-navigation").style.height = nav_height + 'px';
        let nav_top = nav.offsetTop;
        let is_sticky = false;
        let last_scroll_top = 0;
        window.addEventListener('scroll', () => {
          let scroll_top = document.documentElement.scrollTop || document.body.scrollTop;
          if(scroll_top > nav_top){
            nav.classList.add('sticky');
            Shopify.productNav.isOpen = true;

          }
          if(scroll_top < nav_top){
            nav.classList.remove('sticky');
            Shopify.productNav.isOpen = false;
          }
          // 根据当前的滚动距离计算在哪个tab-area
          this.updateActiveTab(scroll_top);
        });
      }
      updateActiveTab(scrollTop) {
        let newActiveTab = null;
        this.tabAreas.forEach((tabArea, index) => {
          const tabTop = tabArea.offsetTop - 100; // 减去一个偏移量，使切换更早一些
          const tabBottom = tabTop + tabArea.offsetHeight;
          
          if (scrollTop >= tabTop && scrollTop < tabBottom) {
            newActiveTab = this.tabs[index];
          }
        });

        if (newActiveTab && newActiveTab !== this.currentTab) {
          this.tabs.forEach(tab => tab.classList.remove('tab-active'));
          newActiveTab.classList.add('tab-active');
          this.setMover(newActiveTab);
          this.currentTab = newActiveTab;
        }
      }
      initMoverEvent(){
        this.setMover(this.querySelector(".product-tab-item"));
        // 鼠标经过product-tab-item时将mover定位到该li下面对齐
        this.tabs.forEach(el => {
          el.addEventListener('mouseenter', () => {
            this.tabs.forEach(el => { el.classList.remove('active'); });
            el.classList.add('active');
            this.setMover(el);
          });
        });
        // 给.product-tab-list添加鼠标离开事件 获取.tab-active调用setMover
        this.querySelector('.product-tab-list').addEventListener('mouseleave', () => {
          this.tabs.forEach(el => { el.classList.remove('active'); });
          this.setMover(this.querySelector('.tab-active') || this.querySelector('.product-tab-item'));
        });
      }
      initTabEvent(){
        this.tabs.forEach(el => {
          el.addEventListener('click', () => {
            this.tabs.forEach(el => {
              el.classList.remove('tab-active');
            });
            el.classList.add('tab-active');
            this.setMover(el);
            this.widgetCallback();
          });
        });
      }

      widgetCallback(){
        // 获取images-with-text-scrolling调用_onScroll方法
        let imageScrolling = document.querySelectorAll('images-with-text-scrolling');
        imageScrolling.forEach(el => {
          inView3(el, el._reveal.bind(el))
        });
      }

      setMover(el){
        if(!el) return;
        this.mover.style.left = el.offsetLeft + 'px';
        this.mover.style.width = el.offsetWidth + 'px';
      }
    });
  }

  customElements.define('product-size', class extends HTMLElement {
    constructor() {
      super();

      this.action = this.querySelectorAll('.size__button[data-action]');

      if (this.action) {
        this.initAction();
      }
    } 

    initAction() {
      this.action.forEach((action) => {
        action.addEventListener('click', () => {
          this.action.forEach((action) => {
            action.classList.remove('active');
          });
          action.classList.add('active');
          this.setAttribute('data-action', action.dataset.action);
        }); 
      });
    }
  });

  
  customElements.get("g-point") || customElements.define("g-point", class extends HTMLElement {
    constructor() {
      super()
    }

    connectedCallback() {
      this.handle = this.querySelector("[aria-handle]");
      this.content = this.querySelector("[aria-content]");
      this.handle.addEventListener('click', this.onClick.bind(this));
    }

    onClick(){
      this.content.classList.toggle('active');
    }

    hide(){
      this.content.classList.remove('active');
    }

    show(){
      this.content.classList.add('active');
    }
  });
  
  customElements.get("g-area") || customElements.define("g-area", class extends HTMLElement {
    constructor() {
      super();
      // 持续时间
      this.duration = 5000;
      this.currentIndex = 0;
      this.intervalId = null;
      this.next = null;
    }

    connectedCallback() {
      this.points = this.querySelectorAll("[aria-point]");
      this.autoplay = this.getAttribute("autoplay") == "true";
      this.points.forEach(e => {
        this.initPointEvent(e);
      
        if(this.autoplay){
          // 添加鼠标进入和离开事件
          e.addEventListener('mouseenter', this.stopRotation.bind(this));
          e.addEventListener('mouseleave', this.startRotation.bind(this));
        }

      });

      document.addEventListener('click', this.handleClick.bind(this));

      if(this.autoplay){
        // 开始轮询
        this.startRotation();
      }
    }

    
    startRotation() {
      if (this.intervalId) return; // 如果已经在轮询，就不再启动新的轮询

      const rotate = () => {
        this.clear();
        this.growUp(this.points[this.currentIndex]);
        this.currentIndex = (this.currentIndex + 1) % this.points.length;
      };

      clearTimeout(this.next);
      this.next = setTimeout(() => {
        rotate();
      }, 3000);
      this.intervalId = setInterval(() => {
        rotate();
      }, this.duration); // 5秒轮询一次
    }

    stopRotation() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    
    handleClick(event) {
      // 检查点击的目标是否是 this.points 中的元素或其子元素
      const isPointClick = Array.from(this.points).some(point => 
        point === event.target || point.contains(event.target)
      );

      // 如果点击的不是 point 元素或其子元素，执行 clear 方法
      if (!isPointClick) {
        this.clear();
      }
    }

    initPointEvent(el){
      let ariaHandle = el.querySelector("[aria-handle]");
      ariaHandle.addEventListener('click', () => {
        if(el.getAttribute("aria-point") == "show"){
          this.digestion(el);
        }else{
          this.clear();
          this.growUp(el);
        }
      });

      el.addEventListener('mouseenter', () => {
        if(el.timer) clearTimeout(el.timer);
        if(el.getAttribute("aria-point") == "hide"){
          this.clear();
          this.growUp(el);
        }
      });

      el.addEventListener('mouseleave', () => {
        el.timer = setTimeout(() => {
          this.digestion(el);
        }, 3000);
      });

      ariaHandle = null;
    }

    clear(){
      this.points.forEach(e => {
        if(e.getAttribute("aria-point") == "show"){
          this.digestion(e);
        }
      })
    }

    digestion(el){
      el.querySelector("[aria-content]").animate([
        { transform: "scale(1) translate(-50%)", left: "50%", bottom: "32px" },
        { transform: "scale(0) translate(-50%)", left: "-700%", bottom: "-140px" },
      ], {
        duration: 200,
        iterations: 1,
      }).finished.then((e) => {
        el.setAttribute("aria-point", "hide");
        el.style.zIndex = 1;
      });
    }

    growUp(el){
      el.querySelector("[aria-content]").animate([
        { transform: "scale(0) translate(-50%)", display: "block", left: "-700%", bottom: "-140px" },
        { transform: "scale(1) translate(-50%)", left: "50%", bottom: "32px" },
      ], {
        duration: 200,
        iterations: 1,
      }).finished.then((e) => { 
        el.setAttribute("aria-point", "show");
        el.style.zIndex = 6;
      });
    }

  });

  customElements.get("show-viewer") || customElements.define("show-viewer", class extends HTMLElement {
    constructor() {
      super();
      this.viewer = null;
      this.working = false;
    }

    connectedCallback() {
      this.productUrl = this.getAttribute('aria-product');
      const onClick = throttle(this.onClick.bind(this), 1000);
      this.addEventListener('click', onClick);
    }

    onClick(){
      if(!this.hasAttribute('loaded'))
        this.loadViewerModel();
      else
        this.show();
    }

    loadViewerModel(){
      if(!this.productUrl) return;
      this.classList.add("loading");
      try{
        fetch(`${this.productUrl}?section_id=widget-3d-model`).then((response) => response.text()).then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const viewer = html.querySelector('model-viewer');
          this.drawer = new Shopify.Drawer({
            el: viewer,
            classes: "viewer-modal center",
            theme: "dark"
          });
          this.setAttribute("loaded", "");
          this.show();
        }).catch((e) => {
          console.error(e);
        });
      }finally{
        setTimeout(() => {
          this.classList.remove("loading");
        }, 800);
      }
    }

    show(){
      this.drawer.open();
    }
  });

  class StickyHeader extends HTMLElement {
    constructor() {
      super();
      this.opened = false;
    }

    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always' || this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};

      this.setHeaderHeight();

      window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      };

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => this.preventReveal = true;

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);

      this.createObserver();

      this.initMenuHoverEvent();
      this.querySelectorAll("custom-details").forEach(this.initSubMenuEvent);

      this.querySelector(".header-dialog").addEventListener("click", () => {
        document.querySelectorAll('[id^="Details-"][open]').forEach(details => details.close(false));
        this.setAttribute("menu", event.detail);
      });

      document.addEventListener("header:menu:toggle", (event) => {
        this.setAttribute("menu", event.detail);
        if(!this.opened){
          this.loadMenuImage(true);
          this.opened = true;
        }
      });

      this.loadMenuImage(false);

      this.updateMenuTrigger();
      // 监听窗口变化更新导航事件方式
      window.addEventListener("resize", () => {
        this.updateMenuTrigger();
      });
    }

    updateMenuTrigger(){
      // 如果宽度小于1400所有custom-details的trigger值变为click否则hover
      if (window.innerWidth < 1400) {
        this.querySelectorAll("custom-details").forEach((item) => {
          item.setAttribute("trigger", "click");
        });
      } else {
        this.querySelectorAll("custom-details").forEach((item) => {
          item.setAttribute("trigger", "hover");
        });
      }
    }

    // 加载菜单图片， all: true为加载所有图片  false为加载当前需显示的图片
    loadMenuImage(all = false) {
      document.querySelectorAll(all ? ".mega-menu__body-wrapper .g-image.awaiting" : ".mega-menu__body-wrapper.active .g-image.awaiting").forEach((item) => {
        let image = document.createElement("img");
        image.src = item.getAttribute("src");
        item.appendChild(image);
        item.classList.remove("awaiting");
        image = null;
      });
    }

    initSubMenuEvent(details) {
      // 鼠标经过.mega-menu__link[aria-controls]时，对应的mega-menu__product-list添加active类
      let megaMenuLinks = details.querySelectorAll('.mega-menu__link[aria-controls]');
      let megaMenuProductLists = details.querySelectorAll('.mega-menu__body-wrapper');

      megaMenuLinks.forEach((link) => {
        link.addEventListener('mouseenter', (e) => {
          megaMenuLinks.forEach((item) => {
            item.classList.remove('active');
          });
          link.classList.add('active');
          megaMenuProductLists.forEach((item) => {
            item.classList.remove('active');
          });
          if(link.getAttribute('aria-controls')){
            document.querySelector(`#${link.getAttribute('aria-controls')}`)?.classList?.add('active')
          }
        });
      });
    }

    initMenuHoverEvent(details){
      // 鼠标进入.header-wrapper时添加header-wrapper-hover类，移出时删除
      this.addEventListener('mouseenter', throttle(() => {
        this.classList.add('header-wrapper-hover');
      }));
      this.addEventListener('mouseleave', () => {
        this.classList.remove('header-wrapper-hover');
      });
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      document.documentElement.style.setProperty('--announcement-height', `${document.querySelector(".ia-announcement-bar")?.getBoundingClientRect()?.height || 0}px`);

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;
      // if (Shopify?.productNav?.isOpen) return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);

          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (Shopify?.productNav?.isOpen){
        // if(Shopify.back_to_top){
        //   if(!Shopify.back_to_top_btn){
        //     Shopify.getBackBtn();
        //   }
        //   Shopify.back_to_top_btn.classList.remove("back-to-top--visible");
        // }
        this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
        return;
      }
      if (this.headerIsAlwaysSticky) return;
      // 给:root设置css变量--fixed-height为0px
      document.documentElement.style.setProperty('--fixed-height', '0px');

      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();

      this.setAttribute("menu", "close");
    }

    reveal() {
      if (Shopify?.productNav?.isOpen){
        // if(Shopify.back_to_top){
        //   if(!Shopify.back_to_top_btn){
        //     Shopify.getBackBtn();
        //   }
        //   Shopify.back_to_top_btn.classList.add("back-to-top--visible");
        // }
        this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
        return;
      }
      if (this.headerIsAlwaysSticky) return;
      document.documentElement.style.setProperty('--fixed-height', `${this.header.offsetHeight}px`);
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
      // if(Shopify.back_to_top){
      //   if(!Shopify.back_to_top_btn){
      //     Shopify.getBackBtn();
      //   }
      //   Shopify.back_to_top_btn.classList.add("back-to-top--visible");
      // }
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      document.documentElement.style.setProperty('--fixed-height', `${this.header.offsetHeight}px`);
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach(disclosure => disclosure.close());
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      this.searchModal.close(false);
    }
  }

  customElements.define('sticky-header', StickyHeader);

  
class HeroProductCard extends HTMLElement {
  constructor() {
    super();

    this.variants = this.getSelectedVariants();
  }

  connectedCallback() {
    this.form = this.querySelector('form[is="product-form"]');
    if (!this.form) return;

    this.variantInput = this.form.querySelector('input[name="id"]');

    // this.onVariantChangeUnsubscriber = theme.pubsub.subscribe(
    //   theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
    //   this.handleOptionValueChange.bind(this)
    // );
  }

  handleOptionValueChange({ data: { event, target, selectedOptionNames } }) {
    if (!this.contains(event.target)) return;

    this.currentVariant = this.variants.find((variant) => {
      return selectedOptionNames.every((optionName) => variant.options.includes(optionName));
    });

    console.log(this.currentVariant);
    if (this.currentVariant) {
      this.variantInput.value = this.currentVariant.id;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  getSelectedVariants() {
    const selectedVariant = this.querySelector(`[data-variants]`)?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber();
  }
}
customElements.define('hero-product-card', HeroProductCard);

class HeroCollectionTabs extends HTMLElement {
  constructor() {
    super();

    this.isMobile = window.innerWidth <= 768;
    this.style.setProperty("--header-height", document.querySelector("sticky-header").clientHeight + "px");
  }

  connectedCallback() {
    // 使用滚动事件实现固定定位
    this.previousScrollY = window.scrollY;
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll);
    
    // 初始获取元素位置信息
    this.elementRect = this.getBoundingClientRect();
    this.offsetTop = this.offsetTop;

    this.tabs = this.querySelectorAll('[role="tab"]');
    this.targets = {};
    this.tabBlocks = this.querySelectorAll('.tabs-list > .shopify-block');
    this.tabBlocks.forEach((block, index) => {
      block.classList.add(index === 0 ? 'active' : 'noactive');
    });
    this.classList.remove("init-active");
    this.tabs.forEach((tab, index) => {
      if(document.querySelector(tab.getAttribute('href')))
        this.targets[tab.getAttribute('href')] = { index, element: document.querySelector(tab.getAttribute('href')) };
      tab.addEventListener('click', () => {
        this.setActiveBlock(index);
        const target = this.targets[tab.getAttribute('href')].element;
        if (target) {
          window.scrollTo({
            top: target.offsetTop - 70,
            behavior: 'smooth'
          });
        }
      });
    });

    // 监听所有target， 滚动到距离窗口顶部100px时，添加active类
    this.blockObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.boundingClientRect.top <= 150 && entry.boundingClientRect.top >= 0) {
          this.setActiveBlock(this.targets[`#${entry.target.id}`].index);
        }
      });
    },
    {
      threshold: [0.1, 0.2, 0.3, 0.7, 0.8, 0.9, 1],
    });
    Object.values(this.targets).forEach((target) => {
      this.blockObserver.observe(target.element);
    });
  }

  setActiveBlock(index){
    this.tabBlocks.forEach((block, i) => {
      if (i === index) {
        block.classList.add('active');
      }else{
        block.classList.remove('active');
      }
    });
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    const isScrollingDown = currentScrollY > this.previousScrollY;
    
    // 获取当前元素位置
    const rect = this.getBoundingClientRect();
    
    // 向下滚动且元素超出窗口顶部时添加fixed类
    if (!isScrollingDown && rect.bottom <= 0) {
      this.classList.add('fixed');
    } else {
      // 向上滚动到元素位置时移除fixed类
      this.classList.remove('fixed');
    }
    
    this.previousScrollY = currentScrollY;
  }
  
  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    this.blockObserver.disconnect();
  }
}
customElements.define('hero-collection-tabs', HeroCollectionTabs);


window.isMobile = function() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/android|iPad|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) return true;
  if (window.innerWidth <= 768) return true;
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return true;
  if (window.matchMedia("(max-width: 768px)").matches) return true;
  return false;
}
customElements.get("product-awards") || customElements.define("product-awards", class ProductAwards extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.contents = this.querySelectorAll(".awards-content-item");
    this.count = this.getAttribute("data-count");
    if(window.isMobile() || this.count > 5)
      this.initSwiper();
    else
      this.initClickEvent();
  }
  initClickEvent(){
    this.querySelector(".swiper-wrapper").classList.add("flex-swiper");
    this.slides = this.querySelectorAll(".swiper-slide");
    this.slides.forEach((e, index) => {
      e.addEventListener("click", () => {
        let activeIndex = index;
        this.contents.forEach(e => e.classList.remove('active'));
        this.contents[activeIndex].classList.add('active');
        
        this.slides.forEach(e => e.classList.remove('swiper-slide-active'));
        e.classList.add('swiper-slide-active');
        
      });
    });
  }
  initSwiper(){
    this.swiper = new Swiper('.product-awards-swiper .swiper', {
      slidesPerView: 1,
      initialSlide: 0,
      loop: true,
      centeredSlides: false,
      slideToClickedSlide: true,
      spaceBetween: 20,
      breakpoints: {
        768: {
          slidesPerView: "auto"
        }
      },
      on: {
        slideChange: ({ activeIndex }) => {
          if(activeIndex >= this.contents.length)
            activeIndex = activeIndex - this.contents.length;
          if(activeIndex >= this.contents.length)
            activeIndex = activeIndex - this.contents.length;
          this.contents.forEach(e => e.classList.remove('active'));
          this.contents[activeIndex].classList.add('active');
        }
      }
    });
  }
});


customElements.get("page-anchors") || customElements.define("page-anchors", class extends HTMLElement {
  constructor() {
    super();
    this.sections = [];
    this.currentTab = null;
    this.scrollOffset = 100;
    this.mobileScrollOffset = 16;
    this.onScroll = null;
    this.onResize = null;
    this.onScrollSticky = null;
    this.isScrollingToAnchor = false;
    this.scrollEndTimer = null;
    this.tabScrollTimer = null;
    this.moverFrame = null;
    this.moverObserver = null;
  }

  connectedCallback() {
    this.mover = this.querySelector(".mover");
    this.tabList = this.querySelector(".product-tab-list");
    this.navWrapper = this.querySelector(".product-nav-wrapper");
    this.tabs = this.querySelectorAll(".product-tab-item");
    this.nav = this.closest(".product-nav");
    this.navigation = this.closest(".product-navigation");
    this.stickyHeader = document.querySelector("sticky-header");
    this.enableSticky = this.nav?.hasAttribute("sticky");

    this.sections = Array.from(this.querySelectorAll("[data-anchor-item]")).map((item) => ({
      item,
      tab: item.closest(".product-tab-item"),
      target: this.resolveTarget(item),
    }));

    this.initMoverEvents();
    this.initClickEvents();
    this.initMoverObserver();

    if (this.enableSticky) {
      this.initSticky();
      requestAnimationFrame(() => {
        this.updateNavHeight();
        this.scheduleMoverUpdate(this.getActiveTab());
      });
    }

    this.initScrollSpy();
    this.updateActiveTab(this.getScrollTop());
    this.scheduleMoverUpdate(this.getActiveTab());

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.scheduleMoverUpdate(this.getActiveTab()));
    }
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);
    this.onScroll?.cancel?.();
    this.onResize?.cancel?.();
    clearTimeout(this.scrollEndTimer);
    clearTimeout(this.tabScrollTimer);
    cancelAnimationFrame(this.moverFrame);
    this.moverObserver?.disconnect();
  }

  getActiveTab() {
    return this.querySelector(".tab-active") || this.tabs[0];
  }

  getMoverTarget(tab) {
    return tab?.querySelector("[data-anchor-item]") || tab;
  }

  scheduleMoverUpdate(tab) {
    const targetTab = tab || this.getActiveTab();
    cancelAnimationFrame(this.moverFrame);
    this.moverFrame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.setMover(targetTab);
      });
    });
  }

  initMoverObserver() {
    if (typeof ResizeObserver === "undefined") return;

    this.moverObserver = new ResizeObserver(() => {
      this.updateNavHeight();
      this.scheduleMoverUpdate(this.getActiveTab());
    });

    if (this.navWrapper) this.moverObserver.observe(this.navWrapper);
    if (this.tabList) this.moverObserver.observe(this.tabList);
    if (this.nav) this.moverObserver.observe(this.nav);
  }

  getScrollTop() {
    return document.documentElement.scrollTop || document.body.scrollTop;
  }

  getElementTop(element) {
    return element.getBoundingClientRect().top + this.getScrollTop();
  }

  resolveTarget(item) {
    const href = item.getAttribute("href");
    if (!href?.startsWith("#")) return null;

    let target = document.querySelector(href);
    if (target) return target;

    const id = href.slice(1);
    if (id.startsWith("area-")) {
      target = document.getElementById(id.slice(5));
      if (target) return target;
    }

    return document.getElementById(id) || null;
  }

  getScrollOffset() {
    const stickyHeight = this.nav?.classList.contains("sticky") ? this.nav.offsetHeight : 0;
    const offset = window.innerWidth <= 768 ? this.mobileScrollOffset : this.scrollOffset;
    return stickyHeight + offset;
  }

  updateNavHeight() {
    if (!this.navigation || !this.nav) return;

    const wasSticky = this.nav.classList.contains("sticky");
    if (wasSticky) this.nav.classList.remove("sticky");

    const navHeight = Math.max(
      this.nav.offsetHeight,
      this.nav.scrollHeight,
      this.nav.getBoundingClientRect().height
    );

    if (wasSticky) this.nav.classList.add("sticky");

    if (navHeight > 0) {
      this.navigation.style.height = `${navHeight}px`;
    } else {
      this.navigation.style.removeProperty("height");
    }
  }

  isMobile() {
    return window.innerWidth <= 768;
  }

  getMinScrollTop() {
    if (!this.enableSticky || this.navTop == null) return 0;
    return this.navTop + 1;
  }

  keepHeaderHidden() {
    if (!Shopify.productNav) {
      Shopify.productNav = { isOpen: false };
    }
    Shopify.productNav.isOpen = true;
    this.stickyHeader?.dispatchEvent(new Event("preventHeaderReveal"));
    if (this.nav && this.getScrollTop() > this.navTop) {
      this.nav.classList.add("sticky");
    }
  }

  scrollToTarget(target) {
    if (!target) return;

    this.isScrollingToAnchor = true;
    this.keepHeaderHidden();

    let top = this.getElementTop(target) - this.getScrollOffset();
    top = Math.max(top, this.getMinScrollTop(), 0);

    window.scrollTo({ top, behavior: "smooth" });
    this.waitForScrollEnd(() => {
      this.isScrollingToAnchor = false;
      this.onScrollSticky?.();
      this.updateActiveTab(this.getScrollTop());
      this.scheduleMoverUpdate(this.getActiveTab());
    });
  }

  waitForScrollEnd(callback) {
    clearTimeout(this.scrollEndTimer);
    let lastScrollTop = this.getScrollTop();

    const checkScrollEnd = () => {
      const scrollTop = this.getScrollTop();
      if (scrollTop !== lastScrollTop) {
        lastScrollTop = scrollTop;
        this.scrollEndTimer = setTimeout(checkScrollEnd, 100);
        return;
      }
      callback();
    };

    this.scrollEndTimer = setTimeout(checkScrollEnd, 100);
  }

  setMover(tab) {
    if (!this.mover || !tab) return;

    const target = this.getMoverTarget(tab);
    const wrapper = this.navWrapper || this;
    const list = this.tabList;

    if (list && tab.offsetParent === list) {
      const scrollLeft = this.navWrapper?.scrollLeft || 0;
      this.mover.style.left = `${list.offsetLeft + tab.offsetLeft + target.offsetLeft - scrollLeft}px`;
      this.mover.style.width = `${target.offsetWidth}px`;
      return;
    }

    const targetRect = target.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    this.mover.style.left = `${targetRect.left - wrapperRect.left + wrapper.scrollLeft}px`;
    this.mover.style.width = `${targetRect.width}px`;
  }

  scrollTabIntoView(tab) {
    if (!tab || !this.isMobile() || !this.navWrapper) return;

    const wrapper = this.navWrapper;
    const tabLeft = tab.offsetLeft;
    const tabWidth = tab.offsetWidth;
    const scrollLeft = tabLeft - wrapper.clientWidth / 2 + tabWidth / 2;

    wrapper.scrollTo({ left: Math.max(0, scrollLeft), behavior: "smooth" });

    clearTimeout(this.tabScrollTimer);
    const syncMover = () => this.scheduleMoverUpdate(tab);
    const onWrapperScroll = () => {
      clearTimeout(this.tabScrollTimer);
      this.tabScrollTimer = setTimeout(() => {
        wrapper.removeEventListener("scroll", onWrapperScroll);
        syncMover();
      }, 50);
    };

    wrapper.addEventListener("scroll", onWrapperScroll, { passive: true });
    this.tabScrollTimer = setTimeout(syncMover, 300);
  }

  setActiveTab(tab) {
    if (!tab) return;

    if (tab !== this.currentTab) {
      this.tabs.forEach((el) => el.classList.remove("tab-active", "active"));
      tab.classList.add("tab-active");
      this.currentTab = tab;
      this.scrollTabIntoView(tab);
    }

    this.scheduleMoverUpdate(tab);
  }

  initClickEvents() {
    this.sections.forEach(({ item, tab, target }) => {
      item.addEventListener("click", (event) => {
        event.preventDefault();
        if (tab) this.setActiveTab(tab);
        this.scrollToTarget(target);
      });
    });
  }

  initMoverEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener("mouseenter", () => {
        this.tabs.forEach((el) => el.classList.remove("active"));
        tab.classList.add("active");
        this.scheduleMoverUpdate(tab);
      });
    });

    this.querySelector(".product-tab-list")?.addEventListener("mouseleave", () => {
      this.tabs.forEach((el) => el.classList.remove("active"));
      this.scheduleMoverUpdate(this.getActiveTab());
    });

    this.navWrapper?.addEventListener("scroll", () => {
      this.scheduleMoverUpdate(this.querySelector(".active") || this.getActiveTab());
    }, { passive: true });
  }

  initSticky() {
    if (!this.navigation || !this.nav) return;

    this.updateNavHeight();
    this.navTop = this.nav.offsetTop;

    if (!Shopify.productNav) {
      Shopify.productNav = { isOpen: false };
    }

    this.onScrollSticky = () => {
      const wasSticky = this.nav.classList.contains("sticky");
      const scrollTop = this.getScrollTop();

      if (scrollTop > this.navTop) {
        this.nav.classList.add("sticky");
        Shopify.productNav.isOpen = true;
      } else if (!this.isScrollingToAnchor) {
        this.nav.classList.remove("sticky");
        Shopify.productNav.isOpen = false;
      }

      this.updateNavHeight();

      if (wasSticky !== this.nav.classList.contains("sticky")) {
        this.scheduleMoverUpdate(this.getActiveTab());
      }
    };
  }

  initScrollSpy() {
    this.onScroll = throttle2(() => {
      if (this.onScrollSticky) this.onScrollSticky();
      this.updateActiveTab(this.getScrollTop());
    });

    this.onResize = throttle2(() => {
      this.updateNavHeight();
      if (this.nav) {
        this.navTop = this.nav.offsetTop;
      }
      this.scheduleMoverUpdate(this.getActiveTab());
    });

    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onResize);
  }

  updateActiveTab(scrollTop) {
    const offset = this.getScrollOffset();
    let activeTab = null;

    this.sections.forEach(({ target, tab }) => {
      if (!target || !tab) return;
      if (scrollTop >= this.getElementTop(target) - offset - 1) {
        activeTab = tab;
      }
    });

    if (!activeTab && this.sections[0]?.tab) {
      activeTab = this.sections[0].tab;
    }

    if (activeTab) {
      this.setActiveTab(activeTab);
    }
  }
});